// UI redesign v2 preview build - 7 May 2026
/**
 * SyproFlow — Extraction Results Workbench
 * =========================================
 * "Documents In. Data Out."
 *
 * Version: 8.0.0 — UI Redesign per SyproFlow_UI_Redesign_Spec_v1.0
 *
 * v8.0.0 changes (UI redesign — see CHANGELOG at end for full list):
 *  - Three-column desktop layout: collapsible sidebar | source preview | data panel
 *  - Top bar stripped to: logo + doc name + status pill + Settings + user menu
 *  - Bottom action bar with View Source / Re-extract / Export / Push to Tally
 *  - Settings drawer (slide-in) holds Exam, Tier, Batch, Tally, Branding toggles
 *  - Summary tab + per-table tabs inside the data panel
 *  - Editable cells with click-to-edit, Tab/Shift+Tab, Enter (next row same col),
 *    Escape cancel, Ctrl+Z undo (cell edits + row deletes)
 *  - Edited-cell dot indicator; hover reveals the original extracted value
 *  - Low-confidence yellow underline (replaces full-cell background tint)
 *  - Source preview panel (PDF via native iframe; image via <img>)
 *  - Orange brand colour (#EA580C) replaces purple/indigo throughout
 *  - Inter typography via Google Fonts @import; 20/14/11px size scale
 *  - Mobile: single-column tabbed layout (Source | Data | Docs)
 *
 * Preserved verbatim (critical mobile plumbing):
 *  - createImageBitmap() client-side compression (Android FileReader hang fix)
 *  - FormData with raw Blob + filename arg (File constructor ban)
 *  - DOM-append-before-click download pattern
 *  - export-dropdown-container class for click-outside handler
 *  - Synchronous handleFileSelect (Android camera file reference fix)
 *  - All extraction endpoints, payloads, auth headers, session timeout
 *
 * By SyproOne Solutions Pvt Ltd
 */

import React, { useState, useCallback, useRef, useEffect, useMemo } from 'react';
import { Routes, Route, Navigate, useNavigate } from 'react-router-dom';
import {
  Upload, FileText, Download, Trash2, X, Plus, Check,
  Loader2, AlertCircle, CheckCircle, Copy,
  FileSpreadsheet, File, Image as ImageIcon, RefreshCw,
  Search, ChevronDown, Menu,
  Eye, EyeOff, Settings as SettingsIcon,
  Lock, Camera, FolderOpen, PanelLeftClose, PanelLeftOpen,
  User, LogOut, HelpCircle, Building2, Undo2
} from 'lucide-react';
import { checkTallyBridge } from './TallyExport';
import ChatWidget from './ChatWidget';
import FeedbackWidget from './FeedbackWidget';
import AdminDashboard from './AdminDashboard';
import WelcomeScreen from './WelcomeScreen';
import LoginPage from './LoginPage';
import VoucherPreview from './VoucherPreview';
import DownloadBridge from './DownloadBridge';

// =============================================================================
// Configuration
// =============================================================================

const getApiBaseUrl = () => {
  if (typeof window !== 'undefined' && window.SYPROFLOW_CONFIG?.API_BASE_URL) {
    return window.SYPROFLOW_CONFIG.API_BASE_URL;
  }
  if (import.meta?.env?.VITE_API_BASE_URL) {
    return import.meta.env.VITE_API_BASE_URL;
  }
  if (typeof localStorage !== 'undefined') {
    const stored = localStorage.getItem('syproflow_api_base');
    if (stored !== null) return stored;
  }
  if (typeof window !== 'undefined') {
    const host = window.location.hostname;
    if (host === 'app.syproflow.com' || host === 'syproflow.com' || host === 'www.syproflow.com') {
      return 'https://api.syproflow.com';
    }
  }
  return 'http://localhost:8000';
};

const API_BASE = getApiBaseUrl();

// Status pill config — Done / Processing / Failed / Ready (ex-Pending)
const STATUS_CONFIG = {
  PENDING:    { bg: 'bg-slate-100',   text: 'text-slate-600',   label: 'Ready',      dot: 'bg-slate-400' },
  PROCESSING: { bg: 'bg-orange-50',   text: 'text-[#B45309]',   label: 'Processing', dot: 'bg-[#F59E0B]' },
  COMPLETED:  { bg: 'bg-emerald-50',  text: 'text-emerald-700', label: 'Done',       dot: 'bg-emerald-500' },
  FAILED:     { bg: 'bg-red-50',      text: 'text-red-700',     label: 'Failed',     dot: 'bg-red-500' }
};

const getAuthHeaders = () => {
  const token = localStorage.getItem('syproflow_token');
  return token ? { 'Authorization': `Bearer ${token}` } : {};
};

// =============================================================================
// Small utilities
// =============================================================================

const formatFileSize = (bytes) => {
  if (bytes == null) return '';
  if (bytes < 1024) return bytes + ' B';
  if (bytes < 1048576) return (bytes / 1024).toFixed(1) + ' KB';
  return (bytes / 1048576).toFixed(1) + ' MB';
};

const truncateFileName = (name, maxLength = 28) => {
  if (!name || name.length <= maxLength) return name || '';
  const dot = name.lastIndexOf('.');
  if (dot <= 0) return name.slice(0, maxLength - 1) + '…';
  const ext = name.slice(dot + 1);
  const base = name.slice(0, dot);
  const room = Math.max(4, maxLength - ext.length - 2);
  return `${base.slice(0, room)}…${ext}`;
};

// Heuristic: decide whether a column is numeric so we can right-align + use mono font.
const isNumericColumn = (header, rows) => {
  if (!header || !rows || rows.length === 0) return false;
  const lower = String(header).toLowerCase();
  // Strong signals by header name
  if (/(amount|qty|quantity|price|rate|total|subtotal|tax|gst|cgst|sgst|igst|discount|value|balance|cost|number|no\.?)$/i.test(lower)) {
    // …unless it's a document "number" field (treat as text-like for readability)
    if (/^(invoice\s*no|bill\s*no|po\s*no|doc\s*no|ref\s*no|order\s*no)/i.test(lower)) return false;
    return true;
  }
  // Sample up to 10 non-empty cells
  let numeric = 0, checked = 0;
  for (let i = 0; i < rows.length && checked < 10; i++) {
    const v = rows[i]?.[header];
    if (v == null || v === '') continue;
    checked++;
    const stripped = String(v).replace(/[,₹$€£\s]/g, '');
    if (/^-?\d+(\.\d+)?%?$/.test(stripped)) numeric++;
  }
  return checked > 0 && numeric / checked >= 0.7;
};

const isDateColumn = (header) => {
  if (!header) return false;
  return /^(date|due\s*date|invoice\s*date|order\s*date|delivery\s*date|period)$/i.test(String(header).trim());
};

// Overall confidence score: mean across every confidence-annotated cell.
const computeOverallConfidence = (tables) => {
  if (!tables || tables.length === 0) return null;
  let sum = 0, count = 0;
  for (const table of tables) {
    const conf = table.confidence || {};
    for (const rowIdx in conf) {
      for (const field in conf[rowIdx]) {
        const c = conf[rowIdx][field]?.confidence;
        if (typeof c === 'number') { sum += c; count++; }
      }
    }
  }
  if (count === 0) return null;
  return Math.round(sum / count);
};

// Count fields across metadata + table headers (deduped by header name)
const computeFieldsExtracted = (doc) => {
  if (!doc) return 0;
  const headerSet = new Set();
  (doc.tables || []).forEach(t => (t.headers || []).forEach(h => headerSet.add(h)));
  const metaKeys = Object.keys(doc.metadata || {}).filter(k => doc.metadata[k] != null && doc.metadata[k] !== '').length;
  return headerSet.size + metaKeys;
};

// Count total rows across all tables
const computeRowsExtracted = (doc) => {
  if (!doc?.tables) return 0;
  return doc.tables.reduce((sum, t) => sum + ((t.rows || t.data || []).length), 0);
};

// =============================================================================
// TallySettingsModal — GST / voucher classification settings
// Kept from v7 (opened from Settings drawer → Advanced). Preserved unchanged.
// =============================================================================

const TallySettingsModal = ({ isOpen, onClose, settings, onSave }) => {
  const [localSettings, setLocalSettings] = useState(settings || {
    companyName: '', gstin: '', additionalGstins: [],
    defaultVoucherType: 'Auto', defaultGodown: 'Main Location', defaultLedger: 'Cash'
  });
  const [newGstin, setNewGstin] = useState('');

  useEffect(() => { if (settings) setLocalSettings(settings); }, [settings]);

  if (!isOpen) return null;

  const handleSave = () => { onSave(localSettings); onClose(); };
  const addGstin = () => {
    if (newGstin && newGstin.length === 15 && !localSettings.additionalGstins.includes(newGstin)) {
      setLocalSettings(prev => ({ ...prev, additionalGstins: [...(prev.additionalGstins || []), newGstin.toUpperCase()] }));
      setNewGstin('');
    }
  };
  const removeGstin = (g) => setLocalSettings(prev => ({
    ...prev, additionalGstins: (prev.additionalGstins || []).filter(x => x !== g)
  }));

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" onClick={onClose}>
      <div className="bg-white rounded-xl w-full max-w-lg max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
        <div className="sticky top-0 bg-white border-b border-[#E5E7EB] px-5 py-3 flex items-center justify-between">
          <h3 className="text-[16px] font-semibold text-[#111827]">Tally / GST Settings</h3>
          <button onClick={onClose} className="p-1.5 hover:bg-slate-100 rounded-md"><X className="w-4 h-4 text-slate-500" /></button>
        </div>
        <div className="p-5 space-y-4">
          <div>
            <label className="block text-[11px] font-semibold text-[#4B5563] uppercase tracking-wide mb-1.5">Company Name</label>
            <input type="text" value={localSettings.companyName || ''}
              onChange={e => setLocalSettings(prev => ({ ...prev, companyName: e.target.value }))}
              placeholder="SyproOne Solutions Pvt Ltd"
              className="w-full px-3 py-2 text-[14px] border border-[#E5E7EB] rounded-md focus:outline-none focus:border-[#EA580C]" />
          </div>
          <div>
            <label className="block text-[11px] font-semibold text-[#4B5563] uppercase tracking-wide mb-1.5">Primary GSTIN</label>
            <input type="text" value={localSettings.gstin || ''}
              onChange={e => setLocalSettings(prev => ({ ...prev, gstin: e.target.value.toUpperCase() }))}
              placeholder="27AABCS1234A1Z5" maxLength={15}
              className="w-full px-3 py-2 text-[14px] font-mono border border-[#E5E7EB] rounded-md focus:outline-none focus:border-[#EA580C]" />
          </div>
          <div>
            <label className="block text-[11px] font-semibold text-[#4B5563] uppercase tracking-wide mb-1.5">Additional GSTINs</label>
            <div className="flex gap-2 mb-2">
              <input type="text" value={newGstin}
                onChange={e => setNewGstin(e.target.value.toUpperCase())}
                placeholder="Add another GSTIN" maxLength={15}
                className="flex-1 px-3 py-2 text-[14px] font-mono border border-[#E5E7EB] rounded-md focus:outline-none focus:border-[#EA580C]" />
              <button onClick={addGstin} className="px-3 py-2 bg-[#EA580C] text-white rounded-md text-[13px] font-semibold hover:bg-[#C2410C]">Add</button>
            </div>
            {(localSettings.additionalGstins || []).length > 0 && (
              <div className="space-y-1">
                {(localSettings.additionalGstins || []).map(g => (
                  <div key={g} className="flex items-center justify-between px-3 py-1.5 bg-[#F9FAFB] rounded-md">
                    <span className="text-[13px] font-mono text-[#111827]">{g}</span>
                    <button onClick={() => removeGstin(g)} className="text-[#EF4444] hover:text-red-700"><X className="w-3.5 h-3.5" /></button>
                  </div>
                ))}
              </div>
            )}
          </div>
          <div>
            <label className="block text-[11px] font-semibold text-[#4B5563] uppercase tracking-wide mb-1.5">Default Voucher Type</label>
            <select value={localSettings.defaultVoucherType || 'Auto'}
              onChange={e => setLocalSettings(prev => ({ ...prev, defaultVoucherType: e.target.value }))}
              className="w-full px-3 py-2 text-[14px] border border-[#E5E7EB] rounded-md focus:outline-none focus:border-[#EA580C] bg-white">
              <option value="Auto">Auto-detect</option>
              <option value="Sales">Sales</option>
              <option value="Purchase">Purchase</option>
              <option value="Credit Note">Credit Note</option>
              <option value="Debit Note">Debit Note</option>
              <option value="Receipt">Receipt</option>
              <option value="Payment">Payment</option>
              <option value="Journal">Journal</option>
              <option value="Expense">Expense</option>
            </select>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-[11px] font-semibold text-[#4B5563] uppercase tracking-wide mb-1.5">Default Godown</label>
              <input type="text" value={localSettings.defaultGodown || ''}
                onChange={e => setLocalSettings(prev => ({ ...prev, defaultGodown: e.target.value }))}
                className="w-full px-3 py-2 text-[14px] border border-[#E5E7EB] rounded-md focus:outline-none focus:border-[#EA580C]" />
            </div>
            <div>
              <label className="block text-[11px] font-semibold text-[#4B5563] uppercase tracking-wide mb-1.5">Default Ledger</label>
              <input type="text" value={localSettings.defaultLedger || ''}
                onChange={e => setLocalSettings(prev => ({ ...prev, defaultLedger: e.target.value }))}
                className="w-full px-3 py-2 text-[14px] border border-[#E5E7EB] rounded-md focus:outline-none focus:border-[#EA580C]" />
            </div>
          </div>
        </div>
        <div className="sticky bottom-0 bg-white border-t border-[#E5E7EB] px-5 py-3 flex justify-end gap-2">
          <button onClick={onClose} className="px-4 py-2 text-[13px] font-medium text-[#4B5563] hover:bg-slate-100 rounded-md">Cancel</button>
          <button onClick={handleSave} className="px-4 py-2 text-[13px] font-semibold text-white bg-[#EA580C] hover:bg-[#C2410C] rounded-md">Save</button>
        </div>
      </div>
    </div>
  );
};

// =============================================================================
// ProgressBar — orange replaces the old indigo/purple gradient
// =============================================================================

const ProgressBar = ({ progress }) => {
  if (!progress) return null;
  const { percentage = 0, message = '', stage = '', current = 0, total = 0 } = progress;
  const stageIcons = {
    init: '🔄', compress: '📐', upload: '📤',
    reading: '👁️', analyzing: '🔍', extracting: '📊',
    verifying: '✅', formatting: '📋', finalizing: '✨', processing: '🧠'
  };
  const icon = stageIcons[stage] || '⚙️';
  return (
    <div className="w-full space-y-1.5">
      <div className="flex items-center justify-between text-[11px]">
        <span className="text-[#4B5563] font-medium flex items-center gap-1.5">
          <span className="text-sm">{icon}</span>{message}
          <span className="inline-flex">
            <span className="animate-pulse">.</span>
            <span className="animate-pulse" style={{ animationDelay: '0.2s' }}>.</span>
            <span className="animate-pulse" style={{ animationDelay: '0.4s' }}>.</span>
          </span>
        </span>
        <span className="text-[#9CA3AF] tabular-nums">{percentage}%</span>
      </div>
      <div className="w-full bg-[#F3F4F6] rounded-full h-1.5 overflow-hidden">
        <div
          className="h-full rounded-full transition-all duration-700 ease-out"
          style={{
            width: `${percentage}%`,
            background: 'linear-gradient(90deg, #EA580C, #FB923C, #EA580C)',
            backgroundSize: '200% 100%',
            animation: 'sf-shimmer 1.5s infinite linear'
          }}
        />
      </div>
      {total > 0 && (
        <div className="text-[10px] text-[#9CA3AF] text-center">
          {current} of {total} pages
        </div>
      )}
    </div>
  );
};

// =============================================================================
// SourcePreview — renders the original document inline
// PDFs use a native <iframe> (Edge/Chrome built-in viewer handles page nav + scroll).
// Images render with <img> and CSS transform for zoom.
// Non-PDF / non-image types fall back to a "no preview" message.
// =============================================================================

const SourcePreview = ({ file, zoom, onZoomChange }) => {
  const [url, setUrl] = useState(null);
  const [isPdf, setIsPdf] = useState(false);
  const [isImage, setIsImage] = useState(false);

  useEffect(() => {
    if (!file) { setUrl(null); return undefined; }
    const objUrl = URL.createObjectURL(file);
    const name = (file.name || '').toLowerCase();
    const type = (file.type || '').toLowerCase();
    const pdf = type.includes('pdf') || name.endsWith('.pdf');
    const img = type.startsWith('image/') || /\.(jpe?g|png|webp|gif|bmp|heic|heif)$/i.test(name);
    setIsPdf(pdf);
    setIsImage(img);
    setUrl(objUrl);
    return () => URL.revokeObjectURL(objUrl);
  }, [file]);

  if (!file) {
    return (
      <div className="flex-1 flex items-center justify-center text-[#9CA3AF] text-[13px]">
        No source document
      </div>
    );
  }

  // PDF zoom param: Edge/Chrome accept `#zoom=` hash. "Fit" = page, 100 / 150 = percent.
  const pdfUrl = isPdf && url ? `${url}#zoom=${zoom === 'fit' ? 'page-fit' : zoom}&navpanes=0` : null;

  return (
    <div className="flex-1 flex flex-col min-h-0 bg-[#F3F4F6] relative">
      {/* Zoom toolbar */}
      <div className="absolute top-2 right-2 z-10 flex items-center gap-1 bg-white/95 backdrop-blur-sm border border-[#E5E7EB] rounded-md shadow-sm p-0.5">
        <button
          onClick={() => onZoomChange('fit')}
          className={`px-2 py-1 text-[11px] font-medium rounded transition-colors ${zoom === 'fit' ? 'bg-[#EA580C] text-white' : 'text-[#4B5563] hover:bg-[#F9FAFB]'}`}
          title="Fit to page"
        >Fit</button>
        <button
          onClick={() => onZoomChange(100)}
          className={`px-2 py-1 text-[11px] font-medium rounded transition-colors ${zoom === 100 ? 'bg-[#EA580C] text-white' : 'text-[#4B5563] hover:bg-[#F9FAFB]'}`}
        >100%</button>
        <button
          onClick={() => onZoomChange(150)}
          className={`px-2 py-1 text-[11px] font-medium rounded transition-colors ${zoom === 150 ? 'bg-[#EA580C] text-white' : 'text-[#4B5563] hover:bg-[#F9FAFB]'}`}
        >150%</button>
      </div>

      <div className="flex-1 overflow-auto">
        {isPdf && pdfUrl && (
          <iframe
            key={pdfUrl}
            src={pdfUrl}
            title="Source document"
            className="w-full h-full border-0 bg-white"
          />
        )}
        {isImage && url && (
          <div className="min-h-full flex items-start justify-center p-4">
            <img
              src={url}
              alt="Source document"
              className="shadow-[0_2px_8px_rgba(0,0,0,0.08)] bg-white rounded"
              style={{
                maxWidth: zoom === 'fit' ? '100%' : 'none',
                width: zoom === 'fit' ? 'auto' : (zoom === 100 ? '100%' : `${zoom}%`),
                height: 'auto'
              }}
            />
          </div>
        )}
        {!isPdf && !isImage && (
          <div className="h-full flex items-center justify-center text-[#9CA3AF] text-[13px] p-4 text-center">
            <div>
              <FileText className="w-10 h-10 mx-auto mb-2 opacity-40" />
              <p>No inline preview for this file type.</p>
              <p className="text-[11px] mt-1">The extracted data is shown in the right panel.</p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

// =============================================================================
// EditableCell — click to edit; yellow underline for low-confidence; orange dot for edits.
// Keyboard: Tab / Shift+Tab move horizontally; Enter moves to next row same col;
// Escape cancels; onBlur commits. Hover on an edited cell shows the original value.
// =============================================================================

const EditableCell = ({
  value, originalValue, isEdited,
  confidence, isLowConfidence,
  isEditing, editValue,
  onStartEdit, onChangeEdit, onCommitEdit, onCancelEdit,
  onNavigate,
  isNumeric, isDate
}) => {
  const align = isNumeric ? 'text-right' : (isDate ? 'text-center' : 'text-left');
  const fontClass = isNumeric ? 'font-mono tabular-nums' : '';

  const displayValue = value == null || value === '' ? '—' : String(value);
  const isEmpty = value == null || value === '';

  const confPct = confidence?.confidence;
  const tooltip = isEdited
    ? `Edited — originally: ${originalValue == null || originalValue === '' ? '(blank)' : originalValue}`
    : (confPct != null ? `Confidence: ${confPct}%` : null);

  if (isEditing) {
    return (
      <td className={`px-3 py-2 border-b border-[#F3F4F6] relative ${align}`}>
        <input
          type="text"
          value={editValue}
          onChange={(e) => onChangeEdit(e.target.value)}
          onBlur={() => onCommitEdit()}
          onKeyDown={(e) => {
            if (e.key === 'Enter') { e.preventDefault(); onCommitEdit('down'); }
            else if (e.key === 'Escape') { e.preventDefault(); onCancelEdit(); }
            else if (e.key === 'Tab') {
              e.preventDefault();
              onCommitEdit(e.shiftKey ? 'left' : 'right');
            }
          }}
          autoFocus
          className={`w-full px-1.5 py-0.5 text-[14px] border-2 border-[#EA580C] rounded outline-none bg-white ${align} ${fontClass}`}
          style={{ minHeight: 24 }}
        />
      </td>
    );
  }

  return (
    <td
      className={`px-3 py-2 border-b border-[#F3F4F6] cursor-text relative ${align} ${fontClass} group/cell`}
      onClick={onStartEdit}
      title={tooltip || undefined}
    >
      <span
        className={[
          'inline-block max-w-full truncate',
          isEmpty ? 'text-[#9CA3AF]' : 'text-[#111827]',
          isLowConfidence && !isEdited ? 'border-b-2 border-[#F59E0B] pb-0.5' : ''
        ].join(' ')}
      >
        {displayValue}
      </span>
      {isEdited && (
        <span
          className="absolute top-1.5 right-1.5 w-1.5 h-1.5 rounded-full bg-[#EA580C]"
          aria-label="Edited"
        />
      )}
    </td>
  );
};

// =============================================================================
// SummaryTab — document metadata + extraction info
// Shows key-value metadata on top; small extraction stats block at the bottom.
// No timestamps, no model tier, no brand (per spec §7).
// =============================================================================

const SummaryTab = ({ doc, pilotQuota }) => {
  if (!doc) return null;
  const meta = doc.metadata || {};
  const bank = doc.bank_details || {};
  const terms = doc.terms || [];
  const totalInWords = doc.total_in_words || '';
  const accounting = doc.accounting || null;

  // Flatten a nested object into [label, scalarValue] pairs.
  // e.g. { patient: { name: "X", mr_number: "Y" } } → [["Patient › Name", "X"], ["Patient › Mr Number", "Y"]]
  const flattenEntry = (k, v) => {
    if (v != null && typeof v === 'object' && !Array.isArray(v)) {
      const pairs = [];
      Object.entries(v).forEach(([sk, sv]) => {
        if (sv == null || sv === '') return;
        if (sv != null && typeof sv === 'object' && !Array.isArray(sv)) {
          flattenEntry(`${k} › ${sk}`, sv).forEach(p => pairs.push(p));
        } else {
          pairs.push([`${k} › ${sk}`, sv]);
        }
      });
      return pairs.length > 0 ? pairs : [[k, '(empty)']];
    }
    return [[k, v]];
  };

  // Partition metadata into buckets using simple name heuristics.
  const buckets = { document: [], seller: [], buyer: [], shipping: [], totals: [], other: [] };
  const norm = (s) => String(s || '').toLowerCase();
  Object.entries(meta).forEach(([k, v]) => {
    if (v == null || v === '') return;
    const key = norm(k);
    const expanded = flattenEntry(k, v);
    let bucket;
    if (/(seller|supplier|exporter|from|vendor|issuer)/.test(key)) bucket = buckets.seller;
    else if (/(buyer|customer|consignee|billto|bill_to|recipient|client)/.test(key)) bucket = buckets.buyer;
    else if (/(ship|delivery|port|destination|origin)/.test(key)) bucket = buckets.shipping;
    else if (/(total|subtotal|grand|amount|tax|gst|cgst|sgst|igst|vat|balance|paid|due)/.test(key)) bucket = buckets.totals;
    else if (/(invoice|bill|document|doc|date|po|order|ref|reference|number|type|currency|terms|incoterm|place)/.test(key))
      bucket = buckets.document;
    else bucket = buckets.other;
    expanded.forEach(pair => bucket.push(pair));
  });

  const rowsExtracted = computeRowsExtracted(doc);
  const tablesFound = (doc.tables || []).length;
  const fieldsExtracted = computeFieldsExtracted(doc);
  const overallConf = computeOverallConfidence(doc.tables || []);

  const formatKey = (k) => String(k)
    .replace(/_/g, ' ')
    .replace(/\b(gstin|pan|iec|hsn|sac|po|cgst|sgst|igst|gst|gst_in|boe)\b/gi, m => m.toUpperCase())
    .replace(/\b\w/g, c => c.toUpperCase());

  const formatValue = (v) => {
    if (v == null) return '—';
    if (Array.isArray(v)) return v.filter(x => x != null).join(', ') || '—';
    if (typeof v === 'object') {
      const pairs = Object.entries(v).filter(([, sv]) => sv != null && sv !== '');
      return pairs.map(([sk, sv]) => `${sk}: ${sv}`).join(', ') || '—';
    }
    return String(v);
  };

  const Section = ({ title, entries }) => {
    if (!entries || entries.length === 0) return null;
    const maxLabelLen = Math.max(...entries.map(([k]) => formatKey(k).length));
    const useSingleCol = maxLabelLen > 30;
    return (
      <section className="mb-5">
        <h3 className="text-[11px] font-semibold text-[#4B5563] uppercase tracking-wide mb-2">{title}</h3>
        <dl className={`grid grid-cols-1 ${useSingleCol ? '' : 'md:grid-cols-2'} gap-x-6 gap-y-1.5`}>
          {entries.map(([k, v], i) => (
            <div key={i} className="flex justify-between gap-3 py-1 border-b border-[#F3F4F6]">
              <dt className="text-[13px] text-[#4B5563] flex-shrink-0 min-w-0 truncate" title={formatKey(k)}>{formatKey(k)}</dt>
              <dd className="text-[13px] text-[#111827] font-medium text-right break-words">{formatValue(v)}</dd>
            </div>
          ))}
        </dl>
      </section>
    );
  };

  const hasAnyMetadata = Object.values(buckets).some(arr => arr.length > 0)
    || Object.keys(bank).length > 0 || terms.length > 0 || totalInWords
    || (doc.document_content && String(doc.document_content).trim());

  return (
    <div className="p-5 overflow-auto">
      {!hasAnyMetadata ? (
        <div className="text-center py-10">
          <FileText className="w-10 h-10 mx-auto mb-3 text-[#9CA3AF]" />
          <p className="text-[14px] text-[#4B5563] font-medium">No summary metadata extracted</p>
          <p className="text-[12px] text-[#9CA3AF] mt-1">Check the table tabs for extracted data.</p>
        </div>
      ) : (
        <>
          <Section title="Document" entries={buckets.document} />
          <Section title="Seller / Supplier" entries={buckets.seller} />
          <Section title="Buyer / Consignee" entries={buckets.buyer} />
          <Section title="Shipping / Delivery" entries={buckets.shipping} />
          <Section title="Totals" entries={buckets.totals} />

          {Object.keys(bank).length > 0 && (
            <Section title="Bank Details" entries={Object.entries(bank).filter(([, v]) => v != null && v !== '')} />
          )}

          {totalInWords && (
            <section className="mb-5">
              <h3 className="text-[11px] font-semibold text-[#4B5563] uppercase tracking-wide mb-2">Total in Words</h3>
              <p className="text-[13px] text-[#111827] italic">{totalInWords}</p>
            </section>
          )}

          {terms && terms.length > 0 && (
            <section className="mb-5">
              <h3 className="text-[11px] font-semibold text-[#4B5563] uppercase tracking-wide mb-2">Terms &amp; Conditions</h3>
              <ol className="list-decimal pl-5 space-y-1">
                {terms.map((t, i) => (
                  <li key={i} className="text-[13px] text-[#111827]">{String(t)}</li>
                ))}
              </ol>
            </section>
          )}

          <Section title="Other" entries={buckets.other} />

          {doc.document_content && String(doc.document_content).trim() && (
            <section className="mb-5">
              <h3 className="text-[11px] font-semibold text-[#4B5563] uppercase tracking-wide mb-2">Document Content</h3>
              <div className="bg-[#F9FAFB] rounded-lg p-4 border border-[#E5E7EB] space-y-2">
                {String(doc.document_content).split('\n').filter(p => p.trim()).map((para, i) => (
                  <p key={i} className="text-[13px] text-[#111827] leading-relaxed">{para.trim()}</p>
                ))}
              </div>
            </section>
          )}
        </>
      )}

      {/* Extraction stats (secondary, smaller) */}
      <section className="mt-6 pt-4 border-t border-[#E5E7EB]">
        <h3 className="text-[11px] font-semibold text-[#9CA3AF] uppercase tracking-wide mb-2">Extraction Info</h3>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <div>
            <div className="text-[11px] text-[#9CA3AF]">Rows extracted</div>
            <div className="text-[16px] font-semibold text-[#111827] tabular-nums">{rowsExtracted}</div>
          </div>
          <div>
            <div className="text-[11px] text-[#9CA3AF]">Tables found</div>
            <div className="text-[16px] font-semibold text-[#111827] tabular-nums">{tablesFound}</div>
          </div>
          <div>
            <div className="text-[11px] text-[#9CA3AF]">Fields extracted</div>
            <div className="text-[16px] font-semibold text-[#111827] tabular-nums">{fieldsExtracted}</div>
          </div>
          <div>
            <div className="text-[11px] text-[#9CA3AF]">Overall confidence</div>
            <div className="text-[16px] font-semibold text-[#111827] tabular-nums">
              {overallConf != null ? `${overallConf}%` : '—'}
            </div>
          </div>
        </div>
      </section>

      {/* Inline feedback — matches v7 placement */}
      <div className="mt-6">
        {pilotQuota && (
          <div style={{
            fontSize: '12px', color: '#6b7280', textAlign: 'right',
            padding: '2px 4px', marginBottom: '6px'
          }}>
            {pilotQuota.used} of {pilotQuota.limit} pilot extractions used
            {pilotQuota.remaining === 0 && <span style={{ color: '#dc2626', marginLeft: '6px' }}>· Limit reached</span>}
          </div>
        )}
        <FeedbackWidget />
      </div>
    </div>
  );
};

// =============================================================================
// Touch-capable detection: phones/tablets report (hover: none) as their primary input.
// Row actions stay hover-revealed on desktop, but are always visible on touch.
const IS_TOUCH = typeof window !== 'undefined' && typeof window.matchMedia === 'function' && window.matchMedia('(hover: none)').matches;

// TableTab — editable data table for a single extracted table.
// =============================================================================

const TableTab = ({
  doc, table, tableIdx,
  excludedFields, toggleFieldExclusion,
  editingCell, editValue,
  onStartEdit, onChangeEdit, onCommitEdit, onCancelEdit,
  onDeleteRow, onDuplicateRow
}) => {
  const rawHeaders = table.headers || Object.keys((table.rows || table.data)?.[0] || {});
  const excluded = excludedFields[doc.id] || [];
  const visibleHeaders = rawHeaders.filter(h => !excluded.includes(h));
  const rows = table.rows || table.data || [];

  // Pre-compute numeric/date flags for the table (once per render)
  const colMeta = useMemo(() => {
    const m = {};
    rawHeaders.forEach(h => {
      m[h] = { numeric: isNumericColumn(h, rows), date: isDateColumn(h) };
    });
    return m;
  }, [rawHeaders.join('|'), rows.length]);

  const originalValues = table._originalValues || {};

  return (
    <div className="flex-1 overflow-auto">
      <table className="w-full border-collapse">
        <thead className="sticky top-0 z-10 bg-[#F9FAFB] shadow-[0_1px_0_#E5E7EB]">
          <tr>
            <th className="w-10 px-2 py-2 text-[11px] font-semibold text-[#9CA3AF] text-right border-b border-[#E5E7EB]">#</th>
            {rawHeaders.map((h, idx) => {
              const isExcluded = excluded.includes(h);
              const align = colMeta[h]?.numeric ? 'text-right' : (colMeta[h]?.date ? 'text-center' : 'text-left');
              return (
                <th
                  key={idx}
                  onClick={() => toggleFieldExclusion(doc.id, h)}
                  className={[
                    'px-3 py-2 text-[11px] font-semibold text-[#4B5563] border-b border-[#E5E7EB] cursor-pointer select-none hover:bg-[#F3F4F6] transition-colors',
                    align,
                    isExcluded ? 'opacity-40 line-through' : ''
                  ].join(' ')}
                  title={isExcluded ? 'Click to include this column' : 'Click to exclude this column from exports'}
                >
                  <div className={`flex items-center gap-1 ${align === 'text-right' ? 'justify-end' : align === 'text-center' ? 'justify-center' : ''}`}>
                    <span>{h}</span>
                    {isExcluded && <EyeOff className="w-3 h-3" />}
                  </div>
                </th>
              );
            })}
          </tr>
        </thead>
        <tbody>
          {rows.length === 0 ? (
            <tr>
              <td colSpan={visibleHeaders.length + 1} className="text-center py-10 text-[13px] text-[#9CA3AF]">
                No rows in this table.
              </td>
            </tr>
          ) : rows.map((row, rowIdx) => (
            <tr
              key={rowIdx}
              className={`${rowIdx % 2 === 1 ? 'bg-[#F9FAFB]' : 'bg-white'} hover:bg-[#FFF7ED] group/row transition-colors`}
            >
              <td className="w-10 px-2 py-2 text-right align-top relative">
                <span className={`text-[11px] text-[#9CA3AF] tabular-nums ${IS_TOUCH ? 'hidden' : 'group-hover/row:hidden'}`}>{rowIdx + 1}</span>
                <div className={`${IS_TOUCH ? 'flex' : 'hidden group-hover/row:flex'} items-center justify-end gap-0.5 absolute right-1 top-1/2 -translate-y-1/2`}>
                  <button
                    onClick={(e) => { e.stopPropagation(); onDuplicateRow(doc.id, tableIdx, rowIdx); }}
                    className="p-1 text-[#9CA3AF] hover:text-[#EA580C] hover:bg-white rounded transition-colors"
                    title="Duplicate row"
                  ><Copy className="w-3 h-3" /></button>
                  <button
                    onClick={(e) => { e.stopPropagation(); onDeleteRow(doc.id, tableIdx, rowIdx); }}
                    className="p-1 text-[#9CA3AF] hover:text-[#EF4444] hover:bg-white rounded transition-colors"
                    title="Delete row"
                  ><Trash2 className="w-3 h-3" /></button>
                </div>
              </td>
              {rawHeaders.map((h, colIdx) => {
                if (excluded.includes(h)) return null;
                const value = Array.isArray(row)
                  ? row[rawHeaders.indexOf(h)]
                  : row[h];
                const conf = table.confidence?.[rowIdx]?.[h];
                const confPct = conf?.confidence;
                const isLowConf = typeof confPct === 'number' && confPct < 70;
                const isEditing = editingCell?.docId === doc.id &&
                                  editingCell?.tableIdx === tableIdx &&
                                  editingCell?.rowIdx === rowIdx &&
                                  editingCell?.field === h;
                const origRow = originalValues[rowIdx] || {};
                const isEdited = h in origRow;

                return (
                  <EditableCell
                    key={colIdx}
                    value={value}
                    originalValue={origRow[h]}
                    isEdited={isEdited}
                    confidence={conf}
                    isLowConfidence={isLowConf}
                    isEditing={isEditing}
                    editValue={editValue}
                    onStartEdit={() => onStartEdit(doc.id, tableIdx, rowIdx, h, value)}
                    onChangeEdit={onChangeEdit}
                    onCommitEdit={(dir) => onCommitEdit(dir)}
                    onCancelEdit={onCancelEdit}
                    onNavigate={() => {}}
                    isNumeric={colMeta[h]?.numeric}
                    isDate={colMeta[h]?.date}
                  />
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

// =============================================================================
// Main App
// =============================================================================

function SyproFlowMainApp() {
  // ===== Core state (from v7, preserved) =====
  const [documents, setDocuments] = useState([]);
  const [selectedDocId, setSelectedDocId] = useState(null);
  const [isDragging, setIsDragging] = useState(false);
  const [isExtracting, setIsExtracting] = useState(false);
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState(null);

  const [editingCell, setEditingCell] = useState(null);
  const [editValue, setEditValue] = useState('');

  const [excludedFields, setExcludedFields] = useState({});
  const [pageSelections, setPageSelections] = useState({});
  const [isSearching, setIsSearching] = useState(false);
  const [currentProgress, setCurrentProgress] = useState(null);

  const [batchMode, setBatchMode] = useState(false);
  const [batchId, setBatchId] = useState(null);
  const [batchProgress, setBatchProgress] = useState(null);
  const [batchStatus, setBatchStatus] = useState(null);

  const [showAdmin, setShowAdmin] = useState(false);
  const [tallyBridgeStatus, setTallyBridgeStatus] = useState({ connected: false, tallyConnected: false, company: null });

  // ===== Company / Tally settings (from v7) =====
  const [companySettings, setCompanySettings] = useState(() => {
    const saved = localStorage.getItem('syproflow_company_settings');
    if (saved) { try { return JSON.parse(saved); } catch {} }
    return {
      companyName: '', gstin: '', additionalGstins: [],
      defaultVoucherType: 'Auto', defaultGodown: 'Main Location', defaultLedger: 'Cash',
      // New in v8: per-spec §9
      tallyEnabled: false,
      batchEnabled: false,
      brandCompanyName: '', brandTagline: '', brandFooter: ''
    };
  });

  // Back-compat: if an older user has GSTIN but no explicit tallyEnabled, treat as enabled.
  useEffect(() => {
    if (companySettings.gstin && companySettings.tallyEnabled !== true && companySettings.tallyEnabled !== false) {
      setCompanySettings(prev => ({ ...prev, tallyEnabled: true }));
    }
  }, []); // run once

  const [voucherTypeOverrides, setVoucherTypeOverrides] = useState({});
  const [extractionMode, setExtractionMode] = useState(() => localStorage.getItem('syproflow_mode') || 'auto');
  const [modelTier, setModelTier] = useState(() => localStorage.getItem('syproflow_tier') || 'professional');
  const [standardLimitWarning, setStandardLimitWarning] = useState(null);
  const [pilotQuota, setPilotQuota] = useState(null); // {used, limit, remaining} | null — pilot users only

  // ===== New v8 UI state =====
  const [sidebarCollapsed, setSidebarCollapsed] = useState(
    () => localStorage.getItem('syproflow_sidebar_collapsed') === '1'
  );
  const [showSourcePanel, setShowSourcePanel] = useState(
    () => localStorage.getItem('syproflow_show_source') !== '0'
  );
  const [showSettingsDrawer, setShowSettingsDrawer] = useState(false);
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [showTallySettings, setShowTallySettings] = useState(false);
  const [activeTabIdx, setActiveTabIdx] = useState(-1); // -1 = Summary, 0..N = table index
  const [mobileActiveTab, setMobileActiveTab] = useState('data'); // 'source' | 'data' | 'docs'
  const [showExportDropdown, setShowExportDropdown] = useState(false);
  const [showReextractDropdown, setShowReextractDropdown] = useState(false);
  const [sourceZoom, setSourceZoom] = useState('fit'); // 'fit' | 100 | 150

  // Undo stack (session, in-memory)
  const undoStackRef = useRef([]);

  // Track mode change to clear stale results
  const prevExtractionModeRef = useRef(extractionMode);

  // File input refs
  const fileInputRef = useRef(null);
  const cameraInputRef = useRef(null);
  const sidebarFileInputRef = useRef(null);

  const navigate = useNavigate();

  // =========================================================================
  // Effects
  // =========================================================================

  // Persist settings
  useEffect(() => {
    localStorage.setItem('syproflow_company_settings', JSON.stringify(companySettings));
  }, [companySettings]);
  useEffect(() => { localStorage.setItem('syproflow_mode', extractionMode); }, [extractionMode]);
  useEffect(() => { localStorage.setItem('syproflow_tier', modelTier); }, [modelTier]);
  useEffect(() => { localStorage.setItem('syproflow_sidebar_collapsed', sidebarCollapsed ? '1' : '0'); }, [sidebarCollapsed]);
  useEffect(() => { localStorage.setItem('syproflow_show_source', showSourcePanel ? '1' : '0'); }, [showSourcePanel]);

  // Clear stale results when mode changes
  useEffect(() => {
    if (prevExtractionModeRef.current !== extractionMode) {
      prevExtractionModeRef.current = extractionMode;
      setDocuments(prev => {
        const hasExtracted = prev.some(d => d.status === 'COMPLETED');
        if (hasExtracted) {
          setTimeout(() => {
            const name = extractionMode === 'auto' ? 'Default' : 'Exam';
            setSuccessMessage(`Switched to ${name} mode. Previous results cleared — please re-extract.`);
            setTimeout(() => setSuccessMessage(''), 4000);
          }, 0);
          return prev.map(d => d.status === 'COMPLETED'
            ? { ...d, status: 'PENDING', tables: [], metadata: {}, error: null, _hasEdits: false }
            : d);
        }
        return prev;
      });
    }
  }, [extractionMode]);

  // Close export dropdown when clicking outside
  useEffect(() => {
    const handler = (e) => {
      if (showExportDropdown && !e.target.closest('.export-dropdown-container')) {
        setShowExportDropdown(false);
      }
      if (showReextractDropdown && !e.target.closest('.reextract-dropdown-container')) {
        setShowReextractDropdown(false);
      }
      if (showUserMenu && !e.target.closest('.user-menu-container')) {
        setShowUserMenu(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [showExportDropdown, showReextractDropdown, showUserMenu]);

  // Probe Tally Bridge every 30s when Tally is enabled (for Push-to-Tally visibility hint)
  useEffect(() => {
    let mounted = true;
    const check = () => {
      if (!companySettings.tallyEnabled) return;
      checkTallyBridge().then(status => { if (mounted) setTallyBridgeStatus(status); }).catch(() => {});
    };
    check();
    const t = setInterval(check, 30000);
    return () => { mounted = false; clearInterval(t); };
  }, [companySettings.tallyEnabled]);

  // Global keyboard: Ctrl+Z for undo
  useEffect(() => {
    const onKey = (e) => {
      // Don't steal undo from a text input (the cell editor handles its own undo)
      if (editingCell) return;
      const target = e.target;
      const isTextField = target && (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.isContentEditable);
      if (isTextField) return;
      if ((e.ctrlKey || e.metaKey) && !e.shiftKey && e.key.toLowerCase() === 'z') {
        if (undoStackRef.current.length > 0) {
          e.preventDefault();
          undoLastEdit();
        }
      }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editingCell]);

  // Reset tab selection when switching documents
  useEffect(() => {
    setActiveTabIdx(-1);
    setSourceZoom('fit');
  }, [selectedDocId]);

  const selectedDoc = documents.find(d => d.id === selectedDocId);
  const userEmail = (typeof localStorage !== 'undefined' && localStorage.getItem('syproflow_email')) || '';

  // =========================================================================
  // Logout
  // =========================================================================

  const handleAppLogout = useCallback(() => {
    const token = localStorage.getItem('syproflow_token');
    if (token) {
      fetch(`${API_BASE}/auth/logout`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
      }).catch(() => {});
    }
    localStorage.removeItem('syproflow_token');
    localStorage.removeItem('syproflow_email');
    localStorage.removeItem('syproflow_last_activity');
    window.location.href = '/login';
  }, []);

  // =========================================================================
  // Voucher classification — preserved verbatim from v7
  // =========================================================================

  const classifyVoucherType = useCallback((extractedData, documentName) => {
    if (companySettings.defaultVoucherType !== 'Auto') {
      return { voucherType: companySettings.defaultVoucherType, confidence: 'Manual', reason: 'User default setting' };
    }
    const userGstins = [companySettings.gstin, ...(companySettings.additionalGstins || [])]
      .filter(g => g && g.length === 15).map(g => g.toUpperCase());
    const userCompanyName = (companySettings.companyName || '').toLowerCase().trim();

    let sellerGstin = null, buyerGstin = null, sellerName = null, buyerName = null, documentType = null;
    const docLower = (documentName || '').toLowerCase();
    const allTables = extractedData?.tables || [];

    if (docLower.includes('credit note') || docLower.includes('cn')) documentType = 'Credit Note';
    else if (docLower.includes('debit note') || docLower.includes('dn')) documentType = 'Debit Note';
    else if (docLower.includes('receipt') || docLower.includes('acknowledgement')) documentType = 'Receipt/Payment';
    else if (docLower.includes('bill of entry') || docLower.includes('boe')) documentType = 'Import';
    else if (docLower.includes('shipping bill')) documentType = 'Export';
    else if (docLower.includes('invoice') || docLower.includes('bill') || docLower.includes('tax invoice')) documentType = 'Invoice';

    for (const table of allTables) {
      const headers = table.headers || [];
      const rows = table.rows || table.data || [];
      for (const row of rows) {
        for (const h of headers) {
          const hLower = (h || '').toLowerCase();
          const val = row[h];
          if (!val) continue;
          const valStr = String(val).trim().toUpperCase();
          if (valStr.length === 15 && /^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z]{1}[A-Z0-9]{1}Z[A-Z0-9]{1}$/.test(valStr)) {
            if (hLower.includes('seller') || hLower.includes('supplier') || hLower.includes('from')) sellerGstin = valStr;
            else if (hLower.includes('buyer') || hLower.includes('customer') || hLower.includes('to') || hLower.includes('consignee')) buyerGstin = valStr;
            else if (hLower.includes('gstin') || hLower.includes('gst')) {
              if (!sellerGstin) sellerGstin = valStr;
              else if (!buyerGstin) buyerGstin = valStr;
            }
          }
          const valLower = String(val).toLowerCase();
          if (hLower.includes('seller') || hLower.includes('from') || hLower.includes('supplier')) sellerName = valLower;
          else if (hLower.includes('buyer') || hLower.includes('to') || hLower.includes('customer') || hLower.includes('bill to')) buyerName = valLower;
        }
        if (headers.length === 2) {
          const fieldName = String(row[headers[0]] || '').toLowerCase();
          const fieldValue = row[headers[1]];
          if (!fieldValue) continue;
          const valStr = String(fieldValue).trim().toUpperCase();
          const valLower = String(fieldValue).toLowerCase();
          if (valStr.length === 15 && /^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z]{1}[A-Z0-9]{1}Z[A-Z0-9]{1}$/.test(valStr)) {
            if (fieldName.includes('seller') || fieldName.includes('supplier') || fieldName.includes('our')) sellerGstin = valStr;
            else if (fieldName.includes('buyer') || fieldName.includes('customer') || fieldName.includes('your')) buyerGstin = valStr;
          }
          if (fieldName.includes('seller') || fieldName.includes('from')) sellerName = valLower;
          else if (fieldName.includes('buyer') || fieldName.includes('to') || fieldName.includes('customer')) buyerName = valLower;
        }
      }
    }

    let voucherType = 'Sales', confidence = 'Low', reason = 'Default (no matching criteria found)';
    if (userGstins.length > 0) {
      if (sellerGstin && userGstins.includes(sellerGstin)) {
        voucherType = documentType === 'Credit Note' ? 'Credit Note' : 'Sales';
        confidence = 'High'; reason = 'Seller GSTIN matches your GSTIN';
      } else if (buyerGstin && userGstins.includes(buyerGstin)) {
        voucherType = documentType === 'Debit Note' ? 'Debit Note' : 'Purchase';
        confidence = 'High'; reason = 'Buyer GSTIN matches your GSTIN';
      }
    }
    if (confidence === 'Low' && userCompanyName) {
      if (sellerName && sellerName.includes(userCompanyName)) {
        voucherType = documentType === 'Credit Note' ? 'Credit Note' : 'Sales';
        confidence = 'Medium'; reason = 'Seller name matches your company name';
      } else if (buyerName && buyerName.includes(userCompanyName)) {
        voucherType = documentType === 'Debit Note' ? 'Debit Note' : 'Purchase';
        confidence = 'Medium'; reason = 'Buyer name matches your company name';
      }
    }
    if (documentType === 'Import') { voucherType = 'Purchase'; if (confidence === 'Low') { confidence = 'Medium'; reason = 'Bill of Entry indicates import purchase'; } }
    else if (documentType === 'Export') { voucherType = 'Sales'; if (confidence === 'Low') { confidence = 'Medium'; reason = 'Shipping Bill indicates export sale'; } }
    else if (documentType === 'Receipt/Payment') { if (confidence === 'Low') { voucherType = 'Receipt'; reason = 'Receipt document - verify if money received or paid'; } }

    return { voucherType, confidence, reason, sellerGstin, buyerGstin };
  }, [companySettings]);

  // =========================================================================
  // File handling — preserved verbatim from v7 (sync, no async in handlers)
  // =========================================================================

  const addFilesToDocs = useCallback((files) => {
    if (!files || files.length === 0) return;
    const newDocs = Array.from(files).map(file => ({
      id: `${Date.now()}-${Math.random().toString(36).slice(2)}`,
      name: file.name, file, size: file.size, type: file.type,
      status: 'PENDING', tables: [], accounting: null, cost: null, password: '',
      batchId: batchMode ? batchId : null
    }));
    setDocuments(prev => [...prev, ...newDocs]);
    setSelectedDocId(prev => prev || newDocs[0]?.id || null);
  }, [batchMode, batchId]);

  const handleFileSelect = useCallback((e) => {
    const files = Array.from(e.target.files || []);
    addFilesToDocs(files);
    if (e.target) e.target.value = '';
  }, [addFilesToDocs]);

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    setIsDragging(false);
    addFilesToDocs(Array.from(e.dataTransfer.files));
  }, [addFilesToDocs]);

  const handleDragOver = (e) => { e.preventDefault(); setIsDragging(true); };
  const handleDragLeave = () => setIsDragging(false);

  const updateDocPassword = useCallback((docId, password) => {
    setDocuments(prev => prev.map(d => d.id === docId ? { ...d, password } : d));
  }, []);

  const deleteDocument = useCallback((docId) => {
    if (!window.confirm('Remove this document from the session? Any unsaved edits will be lost.')) return;
    setDocuments(prev => prev.filter(d => d.id !== docId));
    if (selectedDocId === docId) setSelectedDocId(null);
  }, [selectedDocId]);

  const clearAll = useCallback(() => {
    setDocuments([]); setSelectedDocId(null); setError(null); setSuccessMessage(null);
    setBatchMode(false); setBatchId(null); setBatchProgress(null); setBatchStatus(null);
    undoStackRef.current = [];
  }, []);

  // =========================================================================
  // Page selection helpers (preserved verbatim)
  // =========================================================================

  const getPageSelection = useCallback((docId) => {
    return pageSelections[docId] || { mode: 'all', pages: '', selectedPages: [], keyword: '', searchResults: null };
  }, [pageSelections]);

  const updatePageSelection = useCallback((docId, updates) => {
    setPageSelections(prev => ({ ...prev, [docId]: { ...getPageSelection(docId), ...updates } }));
  }, [getPageSelection]);

  const parsePageCount = (pages) => {
    if (!pages) return 0;
    return pages.split(',').flatMap(part => {
      const t = part.trim();
      if (t.includes('-')) { const [s, e] = t.split('-').map(Number); return e - s + 1; }
      return 1;
    }).reduce((a, b) => a + b, 0);
  };

  const searchKeyword = useCallback(async (docId) => {
    const doc = documents.find(d => d.id === docId);
    const selection = getPageSelection(docId);
    if (!doc?.file || !selection.keyword?.trim()) return;
    setIsSearching(true);
    try {
      const formData = new FormData();
      formData.append('file', doc.file);
      formData.append('keyword', selection.keyword.trim());
      const response = await fetch(`${API_BASE}/api/v1/documents/search-pages`, {
        method: 'POST', headers: { ...getAuthHeaders() }, body: formData
      });
      if (!response.ok) throw new Error('Search failed');
      const results = await response.json();
      updatePageSelection(docId, { searchResults: results, selectedPages: results.matches?.map(m => m.page) || [] });
    } catch (err) {
      setError(`Search failed: ${err.message}`);
    } finally {
      setIsSearching(false);
    }
  }, [documents, getPageSelection, updatePageSelection]);

  const togglePageFromSearch = useCallback((docId, page) => {
    const selection = getPageSelection(docId);
    const current = selection.selectedPages || [];
    const newPages = current.includes(page) ? current.filter(p => p !== page) : [...current, page].sort((a, b) => a - b);
    updatePageSelection(docId, { selectedPages: newPages });
  }, [getPageSelection, updatePageSelection]);

  const toggleAllSearchResults = useCallback((docId, select) => {
    const selection = getPageSelection(docId);
    const allPages = selection.searchResults?.matches?.map(m => m.page) || [];
    updatePageSelection(docId, { selectedPages: select ? allPages : [] });
  }, [getPageSelection, updatePageSelection]);

  // =========================================================================
  // Extraction (single & batch) — preserved verbatim from v7, plus we snapshot
  // the original tables on completion to support "Reset all edits".
  // =========================================================================

  const snapshotOriginalTables = (tables) => {
    // Deep copy the tables array so future edits don't mutate the snapshot
    try { return JSON.parse(JSON.stringify(tables || [])); }
    catch { return (tables || []).map(t => ({ ...t })); }
  };

  const extractSingle = useCallback(async (docId) => {
    const doc = documents.find(d => d.id === docId);
    if (!doc || !doc.file) return;

    // Standard tier pre-flight check — skip for images (server compresses them)
    if (modelTier === 'standard') {
      const fileSize = doc.file.size / (1024 * 1024);
      const isImage = doc.file.type.startsWith('image/') ||
                      /\.(jpg|jpeg|png|webp|heic|heif)$/i.test(doc.file.name) ||
                      (doc.file.type === '' && !doc.file.name.toLowerCase().endsWith('.pdf'));
      if (!isImage && fileSize > 40.0) {
        setStandardLimitWarning(`File is ${fileSize.toFixed(1)} MB — Standard tier supports files up to 40 MB. Switch to Professional for this document.`);
        return;
      }
    }
    setIsExtracting(true);
    setError(null);
    setStandardLimitWarning(null);
    setCurrentProgress({ percentage: 10, message: 'Starting extraction...', stage: 'init' });
    setDocuments(prev => prev.map(d => d.id === docId ? { ...d, status: 'PROCESSING' } : d));

    let progressTimer = null;
    try {
      // createImageBitmap compression — Android-safe (no FileReader, no File ctor)
      let fileToUpload = doc.file;
      const isPDF = doc.file.name?.toLowerCase().endsWith('.pdf');
      const isLargeImage = !isPDF && doc.file.size > 500 * 1024;

      if (isLargeImage && typeof createImageBitmap === 'function') {
        setCurrentProgress({ percentage: 15, message: 'Optimizing image...', stage: 'compress' });
        try {
          const bitmap = await createImageBitmap(doc.file);
          let w = bitmap.width, h = bitmap.height;
          const maxDim = 1600;
          if (w > maxDim || h > maxDim) {
            if (w > h) { h = Math.round(h * maxDim / w); w = maxDim; }
            else { w = Math.round(w * maxDim / h); h = maxDim; }
          }
          const canvas = document.createElement('canvas');
          canvas.width = w; canvas.height = h;
          canvas.getContext('2d').drawImage(bitmap, 0, 0, w, h);
          bitmap.close();
          const blob = await new Promise(resolve => canvas.toBlob(resolve, 'image/jpeg', 0.75));
          if (blob && blob.size > 0 && blob.size < doc.file.size) {
            const compressedName = (doc.file.name || 'photo').replace(/\.[^.]+$/, '') + '.jpg';
            blob.name = compressedName;
            fileToUpload = blob;
          }
        } catch (compErr) {
          console.warn('Compression skipped:', compErr);
        }
      }

      const formData = new FormData();
      formData.append('file', fileToUpload, fileToUpload.name || doc.file.name || 'document.jpg');
      if (doc.password) formData.append('password', doc.password);

      const selection = getPageSelection(docId);
      if (selection.mode === 'specific' && selection.pages) formData.append('pages', selection.pages);
      else if (selection.mode === 'keyword' && selection.selectedPages?.length > 0) formData.append('pages', selection.selectedPages.join(','));

      setCurrentProgress({ percentage: 30, message: 'Uploading document', stage: 'upload' });

      let endpoint = `${API_BASE}/api/v1/extract/claude-vision`;
      if (extractionMode === 'exam') endpoint = `${API_BASE}/api/v1/extract/exam-results`;
      else formData.append('model', modelTier);

      const progressStages = [
        { pct: 35, msg: 'Uploading to server', stage: 'upload' },
        { pct: 42, msg: 'AI reading your document', stage: 'reading' },
        { pct: 50, msg: 'Identifying tables and fields', stage: 'analyzing' },
        { pct: 58, msg: 'Extracting line items', stage: 'extracting' },
        { pct: 65, msg: 'Processing numbers and dates', stage: 'extracting' },
        { pct: 72, msg: 'Verifying extracted data', stage: 'verifying' },
        { pct: 78, msg: 'Building structured output', stage: 'formatting' },
        { pct: 82, msg: 'Almost done', stage: 'finalizing' },
      ];
      let stageIdx = 0;
      progressTimer = setInterval(() => {
        if (stageIdx < progressStages.length) {
          const s = progressStages[stageIdx];
          setCurrentProgress({ percentage: s.pct, message: s.msg, stage: s.stage });
          stageIdx++;
        }
      }, 2000);

      const response = await fetch(endpoint, {
        method: 'POST', headers: { ...getAuthHeaders() }, body: formData
      });

      clearInterval(progressTimer);
      setCurrentProgress({ percentage: 88, message: 'Processing response', stage: 'formatting' });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        if (response.status === 429) {
          if (errorData.error === 'pilot_limit_reached') {
            throw new Error(`Pilot limit reached — you have used all ${errorData.limit} extractions.`);
          }
          const usage = errorData.usage || {};
          const window = usage.window_days || 3;
          throw new Error(`Free limit reached (${usage.used || '?'}/${usage.limit || '?'} extractions used in last ${window} days).`);
        }
        throw new Error(errorData.detail || 'Extraction failed');
      }

      setCurrentProgress({ percentage: 95, message: 'Preparing your results', stage: 'finalizing' });
      const result = await response.json();

      if (result.pilot_quota) setPilotQuota(result.pilot_quota);

      setDocuments(prev => prev.map(d =>
        d.id === docId ? {
          ...d,
          status: 'COMPLETED',
          tables: result.tables || [],
          _originalTablesSnapshot: snapshotOriginalTables(result.tables),
          cost: result.cost,
          metadata: result.metadata || {},
          bank_details: result.bank_details || {},
          terms: result.terms || [],
          total_in_words: result.total_in_words || '',
          document_content: result.document_content || '',
          accounting: result.accounting || null,
          extraction_type: result.extraction_type || '',
          students: result.students || [],
          document_info: result.document_info || {},
          _hasEdits: false
        } : d
      ));
      setSuccessMessage(`Extracted ${result.tables?.length || 0} table(s) successfully`);
      setTimeout(() => setSuccessMessage(null), 4000);

    } catch (err) {
      setDocuments(prev => prev.map(d => d.id === docId ? { ...d, status: 'FAILED' } : d));
      setError(err.message);
    } finally {
      if (progressTimer) clearInterval(progressTimer);
      setIsExtracting(false);
      setCurrentProgress(null);
    }
  }, [documents, getPageSelection, extractionMode, modelTier]);

  const startBatchMode = useCallback(() => {
    const newBatchId = `batch-${Date.now()}`;
    setBatchMode(true); setBatchId(newBatchId); setBatchStatus('queued');
    setDocuments(prev => prev.map(d => d.status === 'PENDING' ? { ...d, batchId: newBatchId } : d));
  }, []);

  const cancelBatchMode = useCallback(() => {
    setBatchMode(false); setBatchId(null); setBatchStatus(null); setBatchProgress(null);
    setDocuments(prev => prev.map(d => ({ ...d, batchId: null })));
  }, []);

  const startBatchProcessing = useCallback(async () => {
    const batchDocs = documents.filter(d => d.batchId === batchId && d.status === 'PENDING');
    if (batchDocs.length === 0) return;
    setBatchStatus('processing');
    setBatchProgress({
      total_docs: batchDocs.length, completed_docs: 0, failed_docs: 0,
      total_cost: 0, percentage: 0, elapsed_time: 0
    });
    const startTime = Date.now();
    for (let i = 0; i < batchDocs.length; i++) {
      if (batchStatus === 'cancelled' || batchStatus === 'paused') break;
      const doc = batchDocs[i];
      setDocuments(prev => prev.map(d => d.id === doc.id ? { ...d, status: 'PROCESSING' } : d));
      try {
        const formData = new FormData();
        formData.append('file', doc.file);
        formData.append('model', modelTier);
        const response = await fetch(`${API_BASE}/api/v1/extract/claude-vision`, {
          method: 'POST', headers: { ...getAuthHeaders() }, body: formData
        });
        if (!response.ok) {
          if (response.status === 429) {
            const errorData = await response.json().catch(() => ({}));
            const usage = errorData.usage || {};
            const window = usage.window_days || 3;
            setError(`Free limit reached (${usage.used || '?'}/${usage.limit || '?'} in last ${window} days). Batch stopped.`);
            setBatchStatus('cancelled'); break;
          }
          throw new Error('Extraction failed');
        }
        const result = await response.json();
        setDocuments(prev => prev.map(d => d.id === doc.id ? {
          ...d, status: 'COMPLETED',
          tables: result.tables || [],
          _originalTablesSnapshot: snapshotOriginalTables(result.tables),
          cost: result.cost,
          metadata: result.metadata || {},
          bank_details: result.bank_details || {},
          terms: result.terms || [],
          total_in_words: result.total_in_words || '',
          document_content: result.document_content || '',
          accounting: result.accounting || null,
          _hasEdits: false
        } : d));
        setBatchProgress(prev => ({
          ...prev,
          completed_docs: prev.completed_docs + 1,
          total_cost: prev.total_cost + (result.cost || 0),
          percentage: Math.round(((i + 1) / batchDocs.length) * 100),
          elapsed_time: Math.round((Date.now() - startTime) / 1000)
        }));
      } catch (err) {
        setDocuments(prev => prev.map(d => d.id === doc.id ? { ...d, status: 'FAILED' } : d));
        setBatchProgress(prev => ({
          ...prev,
          failed_docs: prev.failed_docs + 1,
          percentage: Math.round(((i + 1) / batchDocs.length) * 100),
          elapsed_time: Math.round((Date.now() - startTime) / 1000)
        }));
      }
    }
    setBatchStatus('completed');
  }, [documents, batchId, batchStatus, modelTier]);

  const pauseBatch = useCallback(() => setBatchStatus('paused'), []);
  const resumeBatch = useCallback(() => startBatchProcessing(), [startBatchProcessing]);
  const cancelBatch = useCallback(() => setBatchStatus('cancelled'), []);

  const extractAll = useCallback(async () => {
    const pendingDocs = documents.filter(d => d.status === 'PENDING');
    for (const doc of pendingDocs) { await extractSingle(doc.id); }
  }, [documents, extractSingle]);

  // Re-extract: change tier if requested, then run extractSingle again
  const reextractWithTier = useCallback(async (tier) => {
    if (!selectedDoc) return;
    if (tier) setModelTier(tier);
    // Clear undo entries pointing at this doc (the tables are about to be replaced)
    undoStackRef.current = undoStackRef.current.filter(e => e.docId !== selectedDoc.id);
    // Mark doc PENDING so extractSingle runs
    setDocuments(prev => prev.map(d => d.id === selectedDoc.id ? { ...d, status: 'PENDING', _hasEdits: false } : d));
    // Give state a tick, then extract
    setTimeout(() => { extractSingle(selectedDoc.id); }, 0);
  }, [selectedDoc, extractSingle]);

  // =========================================================================
  // Export — preserved verbatim from v7 (handleQuickExport, downloadBlob).
  // IMPORTANT: DOM-append-before-click pattern is load-bearing on mobile.
  // =========================================================================

  const downloadBlob = useCallback(async (blob, filename) => {
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = filename;
    document.body.appendChild(a); a.click(); document.body.removeChild(a);
    setTimeout(() => URL.revokeObjectURL(url), 10000);
  }, []);

  const handleQuickExport = useCallback(async (format) => {
    const hasContent =
      selectedDoc?.tables?.length ||
      selectedDoc?.document_content ||
      (selectedDoc?.metadata && Object.keys(selectedDoc.metadata).length > 0);
    if (!hasContent) { setError('No data to export'); return; }
    setShowExportDropdown(false);

    const allTables = selectedDoc.tables.map((table, idx) => {
      const tableHeaders = table.headers || Object.keys((table.rows || table.data)?.[0] || {});
      const tableExcluded = excludedFields[selectedDoc.id] || [];
      const filteredHeaders = tableHeaders.filter(h => !tableExcluded.includes(h));
      const filteredRows = (table.rows || table.data || []).map(row => {
        const newRow = {};
        filteredHeaders.forEach(h => { newRow[h] = row[h]; });
        // Preserve all underscore-prefixed metadata (e.g., _class)
        Object.keys(row).forEach(k => {
          if (k.startsWith('_')) {
            newRow[k] = row[k];
          }
        });
        return newRow;
      });
      return {
        name: table.title || table.name || `Table ${idx + 1}`,
        headers: filteredHeaders, rows: filteredRows, row_count: filteredRows.length
      };
    });

    try {
      if (format === 'xlsx' || format === 'tally-excel') {
        let voucherInfo = null;
        if (format === 'tally-excel') {
          const override = voucherTypeOverrides[selectedDoc.id];
          voucherInfo = override
            ? { voucherType: override, confidence: 'Manual', reason: 'User selected' }
            : classifyVoucherType(selectedDoc, selectedDoc.name);
        }
        const response = await fetch(`${API_BASE}/api/v1/export/excel`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
          body: JSON.stringify({
            tables: allTables,
            filename: selectedDoc.name.replace(/\.[^.]+$/, ''),
            document_name: selectedDoc.name,
            extraction_type: selectedDoc.extraction_type || "",
            students: selectedDoc.students || [],
            document_info: selectedDoc.document_info || {},
            format: format,
            metadata: selectedDoc.metadata || {},
            bank_details: selectedDoc.bank_details || {},
            terms: selectedDoc.terms || [],
            total_in_words: selectedDoc.total_in_words || '',
            document_content: selectedDoc.document_content || '',
            voucher_type: voucherInfo?.voucherType || 'Sales',
            company_settings: format === 'tally-excel' ? {
              company_name: companySettings.companyName,
              gstin: companySettings.gstin,
              default_godown: companySettings.defaultGodown,
              default_ledger: companySettings.defaultLedger
            } : null,
            accounting: selectedDoc.accounting || null
          })
        });
        if (!response.ok) {
          const errText = await response.text().catch(() => 'Export failed');
          throw new Error(errText);
        }
        const blob = await response.blob();
        const filename = `${selectedDoc.name.replace(/\.[^.]+$/, '')}${format === 'tally-excel' ? '_tally' : ''}.xlsx`;
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url; a.download = filename;
        document.body.appendChild(a); a.click(); document.body.removeChild(a);
        setTimeout(() => URL.revokeObjectURL(url), 10000);
        setSuccessMessage('Excel downloaded!');
        setTimeout(() => setSuccessMessage(null), 3000);
        if (format === 'tally-excel' && voucherInfo) {
          setSuccessMessage(`Exported as ${voucherInfo.voucherType} (${voucherInfo.confidence} confidence)`);
          setTimeout(() => setSuccessMessage(null), 4000);
          return;
        }
      } else if (format === 'csv') {
        let csvContent = '';
        allTables.forEach((table, idx) => {
          if (idx > 0) csvContent += '\n\n';
          csvContent += `# ${table.name}\n`;
          csvContent += table.headers.join(',') + '\n';
          csvContent += table.rows.map(row =>
            table.headers.map(h => {
              const val = row[h] || '';
              return `"${String(val).replace(/"/g, '""')}"`;
            }).join(',')
          ).join('\n');
        });
        // Fallback for non-tabular documents: metadata + content as key-value CSV
        if (!allTables.length) {
          const esc = (v) => `"${String(v).replace(/"/g, '""')}"`;
          csvContent = 'Field,Value\n';
          const flatten = (obj, prefix) => {
            Object.entries(obj).forEach(([k, v]) => {
              const key = prefix ? `${prefix}.${k}` : k;
              if (v && typeof v === 'object' && !Array.isArray(v)) flatten(v, key);
              else if (v !== null && v !== undefined && String(v).trim()) csvContent += `${esc(key)},${esc(v)}\n`;
            });
          };
          if (selectedDoc.metadata) flatten(selectedDoc.metadata, '');
          if (selectedDoc.document_info) flatten(selectedDoc.document_info, '');
          if (selectedDoc.document_content) csvContent += `${esc('document_content')},${esc(selectedDoc.document_content)}\n`;
        }
        const blob = new Blob([csvContent], { type: 'text/csv' });
        await downloadBlob(blob, `${selectedDoc.name.replace(/\.[^.]+$/, '')}.csv`);
      } else if (format === 'json') {
        const jsonContent = JSON.stringify({
          document: selectedDoc.name,
          table_count: allTables.length,
          tables: allTables,
          metadata: selectedDoc.metadata || {},
          bank_details: selectedDoc.bank_details || {},
          terms: selectedDoc.terms || [],
          total_in_words: selectedDoc.total_in_words || '',
          document_content: selectedDoc.document_content || '',
          document_info: selectedDoc.document_info || {},
          accounting: selectedDoc.accounting || null
        }, null, 2);
        const blob = new Blob([jsonContent], { type: 'application/json' });
        await downloadBlob(blob, `${selectedDoc.name.replace(/\.[^.]+$/, '')}.json`);
      } else if (format === 'docx') {
        const response = await fetch(`${API_BASE}/api/v1/export/docx`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
          body: JSON.stringify({
            tables: allTables,
            filename: selectedDoc.name.replace(/\.[^.]+$/, ''),
            document_name: selectedDoc.name,
            metadata: selectedDoc.metadata || {},
            bank_details: selectedDoc.bank_details || {},
            terms: selectedDoc.terms || [],
            total_in_words: selectedDoc.total_in_words || '',
            accounting: selectedDoc.accounting || null,
            // Branding fields from v8 Settings drawer
            brand_company_name: companySettings.brandCompanyName || '',
            brand_tagline: companySettings.brandTagline || '',
            brand_footer: companySettings.brandFooter || ''
          })
        });
        if (!response.ok) {
          const errorData = await response.json().catch(() => ({}));
          throw new Error(errorData.detail || 'Word export failed');
        }
        const blob = await response.blob();
        await downloadBlob(blob, `${selectedDoc.name.replace(/\.[^.]+$/, '')}.docx`);
      } else if (format === 'tally') {
        const override = voucherTypeOverrides[selectedDoc.id];
        const response = await fetch(`${API_BASE}/api/v1/export/tally-xml`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
          body: JSON.stringify({
            tables: allTables,
            filename: selectedDoc.name.replace(/\.[^.]+$/, ''),
            document_name: selectedDoc.name,
            metadata: selectedDoc.metadata || {},
            bank_details: selectedDoc.bank_details || {},
            terms: selectedDoc.terms || [],
            total_in_words: selectedDoc.total_in_words || '',
            accounting: selectedDoc.accounting || null,
            ...(override ? { voucher_type: override } : {})
          })
        });
        if (!response.ok) {
          const errorData = await response.json().catch(() => ({}));
          throw new Error(errorData.detail || 'Tally XML export failed');
        }
        const blob = await response.blob();
        await downloadBlob(blob, `${selectedDoc.name.replace(/\.[^.]+$/, '')}_tally.xml`);
      }
      setSuccessMessage(`Exported ${allTables.length} table(s) to ${format.toUpperCase()}`);
      setTimeout(() => setSuccessMessage(null), 3000);
    } catch (err) {
      setError(`Export failed: ${err.message}`);
    }
  }, [selectedDoc, excludedFields, classifyVoucherType, voucherTypeOverrides, companySettings, downloadBlob]);

  // Preview & Push -- navigate to voucher preview
  const handlePreviewAndPush = useCallback(() => {
    if (!selectedDoc || selectedDoc.status !== 'COMPLETED') return;
    navigate(`/preview/${selectedDoc.id}`, { state: { doc: selectedDoc } });
  }, [selectedDoc, navigate]);

  // =========================================================================
  // Field exclusion
  // =========================================================================

  const toggleFieldExclusion = useCallback((docId, field) => {
    setExcludedFields(prev => {
      const current = prev[docId] || [];
      const isExcluded = current.includes(field);
      return { ...prev, [docId]: isExcluded ? current.filter(f => f !== field) : [...current, field] };
    });
  }, []);

  // =========================================================================
  // Cell editing — click-to-edit, Tab/Shift+Tab/Enter navigation, undo
  // =========================================================================

  // Helper: return visible headers for a given doc+table (respecting exclusions)
  const getVisibleHeaders = useCallback((docId, table) => {
    const rawHeaders = table.headers || Object.keys((table.rows || table.data)?.[0] || {});
    const excluded = excludedFields[docId] || [];
    return rawHeaders.filter(h => !excluded.includes(h));
  }, [excludedFields]);

  const startCellEdit = useCallback((docId, tableIdx, rowIdx, field, value) => {
    setEditingCell({ docId, tableIdx, rowIdx, field });
    setEditValue(value == null ? '' : String(value));
  }, []);

  // Actually apply an edit to the document tree; also push to undo stack.
  const applyCellEdit = useCallback((docId, tableIdx, rowIdx, field, newValue, opts = {}) => {
    const { skipUndo = false } = opts;
    setDocuments(prev => prev.map(doc => {
      if (doc.id !== docId) return doc;
      const newTables = doc.tables.map((table, tIdx) => {
        if (tIdx !== tableIdx) return table;
        const currentRows = table.rows || table.data || [];
        let oldValue;
        const newRows = currentRows.map((row, rIdx) => {
          if (rIdx !== rowIdx) return row;
          if (Array.isArray(row)) {
            const headers = table.headers || [];
            const fieldIdx = headers.indexOf(field);
            if (fieldIdx >= 0) {
              oldValue = row[fieldIdx];
              const newRow = [...row];
              newRow[fieldIdx] = newValue;
              return newRow;
            }
            return row;
          } else {
            oldValue = row[field];
            return { ...row, [field]: newValue };
          }
        });

        // Track original values (first edit wins)
        const newOriginalValues = { ...(table._originalValues || {}) };
        if (!newOriginalValues[rowIdx]) newOriginalValues[rowIdx] = {};
        if (!(field in newOriginalValues[rowIdx])) {
          newOriginalValues[rowIdx][field] = oldValue;
        }

        // Push undo entry
        if (!skipUndo && oldValue !== newValue) {
          undoStackRef.current.push({
            type: 'cell_edit', docId, tableIdx, rowIdx, field, oldValue, newValue
          });
        }

        return {
          ...table,
          rows: table.rows ? newRows : undefined,
          data: table.data ? newRows : undefined,
          _originalValues: newOriginalValues,
          _modified: true
        };
      });
      return { ...doc, tables: newTables, _hasEdits: true };
    }));
  }, []);

  // Commit the currently editing cell. If `dir` is provided, move focus afterwards.
  const saveCellEdit = useCallback((dir) => {
    if (!editingCell) return;
    const { docId, tableIdx, rowIdx, field } = editingCell;
    applyCellEdit(docId, tableIdx, rowIdx, field, editValue);
    setEditingCell(null);
    setEditValue('');

    if (dir) {
      // Navigate to next cell
      const doc = documents.find(d => d.id === docId);
      if (!doc) return;
      const table = doc.tables[tableIdx];
      if (!table) return;
      const visibleHeaders = getVisibleHeaders(docId, table);
      const colIdx = visibleHeaders.indexOf(field);
      const rows = table.rows || table.data || [];
      let nextRowIdx = rowIdx, nextColIdx = colIdx;
      if (dir === 'right') nextColIdx = colIdx + 1;
      else if (dir === 'left') nextColIdx = colIdx - 1;
      else if (dir === 'down') nextRowIdx = rowIdx + 1;
      else if (dir === 'up') nextRowIdx = rowIdx - 1;

      if (nextColIdx >= visibleHeaders.length) { nextColIdx = 0; nextRowIdx = rowIdx + 1; }
      if (nextColIdx < 0) { nextColIdx = visibleHeaders.length - 1; nextRowIdx = rowIdx - 1; }
      if (nextRowIdx < 0 || nextRowIdx >= rows.length) return;

      const nextField = visibleHeaders[nextColIdx];
      const nextRow = rows[nextRowIdx];
      const nextValue = Array.isArray(nextRow)
        ? nextRow[(table.headers || []).indexOf(nextField)]
        : nextRow?.[nextField];
      // Use setTimeout so the onBlur commit has flushed first
      setTimeout(() => startCellEdit(docId, tableIdx, nextRowIdx, nextField, nextValue), 0);
    }
  }, [editingCell, editValue, applyCellEdit, documents, getVisibleHeaders, startCellEdit]);

  const cancelCellEdit = useCallback(() => {
    setEditingCell(null);
    setEditValue('');
  }, []);

  // =========================================================================
  // Row operations — delete, duplicate
  // =========================================================================

  const deleteRow = useCallback((docId, tableIdx, rowIdx) => {
    setDocuments(prev => prev.map(doc => {
      if (doc.id !== docId) return doc;
      const newTables = doc.tables.map((table, tIdx) => {
        if (tIdx !== tableIdx) return table;
        const currentRows = table.rows || table.data || [];
        const deletedRow = currentRows[rowIdx];
        const newRows = currentRows.filter((_, idx) => idx !== rowIdx);
        undoStackRef.current.push({ type: 'row_delete', docId, tableIdx, rowIdx, deletedRow });
        return {
          ...table,
          rows: table.rows ? newRows : undefined,
          data: table.data ? newRows : undefined,
          _modified: true
        };
      });
      return { ...doc, tables: newTables, _hasEdits: true };
    }));
  }, []);

  const duplicateRow = useCallback((docId, tableIdx, rowIdx) => {
    setDocuments(prev => prev.map(doc => {
      if (doc.id !== docId) return doc;
      const newTables = doc.tables.map((table, tIdx) => {
        if (tIdx !== tableIdx) return table;
        const currentRows = table.rows || table.data || [];
        const src = currentRows[rowIdx];
        const copy = Array.isArray(src) ? [...src] : { ...src };
        const newRows = [...currentRows.slice(0, rowIdx + 1), copy, ...currentRows.slice(rowIdx + 1)];
        return {
          ...table,
          rows: table.rows ? newRows : undefined,
          data: table.data ? newRows : undefined,
          _modified: true
        };
      });
      return { ...doc, tables: newTables, _hasEdits: true };
    }));
  }, []);

  // Undo the most recent edit in the stack
  const undoLastEdit = useCallback(() => {
    const entry = undoStackRef.current.pop();
    if (!entry) return;
    if (entry.type === 'cell_edit') {
      // Revert cell to oldValue (skip undo push — otherwise we'd loop)
      applyCellEdit(entry.docId, entry.tableIdx, entry.rowIdx, entry.field, entry.oldValue, { skipUndo: true });
      // Clear the edited-dot indicator for this cell if the original now matches
      setDocuments(prev => prev.map(doc => {
        if (doc.id !== entry.docId) return doc;
        const newTables = doc.tables.map((table, tIdx) => {
          if (tIdx !== entry.tableIdx) return table;
          const orig = { ...(table._originalValues || {}) };
          if (orig[entry.rowIdx]) {
            const rowOrig = { ...orig[entry.rowIdx] };
            if (entry.oldValue === rowOrig[entry.field]) delete rowOrig[entry.field];
            if (Object.keys(rowOrig).length === 0) delete orig[entry.rowIdx];
            else orig[entry.rowIdx] = rowOrig;
          }
          return { ...table, _originalValues: orig };
        });
        return { ...doc, tables: newTables };
      }));
      setSuccessMessage('Undo: cell edit reverted');
      setTimeout(() => setSuccessMessage(null), 2000);
    } else if (entry.type === 'row_delete') {
      setDocuments(prev => prev.map(doc => {
        if (doc.id !== entry.docId) return doc;
        const newTables = doc.tables.map((table, tIdx) => {
          if (tIdx !== entry.tableIdx) return table;
          const currentRows = table.rows || table.data || [];
          const newRows = [...currentRows.slice(0, entry.rowIdx), entry.deletedRow, ...currentRows.slice(entry.rowIdx)];
          return {
            ...table,
            rows: table.rows ? newRows : undefined,
            data: table.data ? newRows : undefined
          };
        });
        return { ...doc, tables: newTables };
      }));
      setSuccessMessage('Undo: row restored');
      setTimeout(() => setSuccessMessage(null), 2000);
    }
  }, [applyCellEdit]);

  // Reset all edits on the selected document to the extraction snapshot
  const resetAllEdits = useCallback(() => {
    if (!selectedDoc) return;
    if (!window.confirm('Reset all edits and deletions to the original extraction?')) return;
    setDocuments(prev => prev.map(d => {
      if (d.id !== selectedDoc.id) return d;
      if (!d._originalTablesSnapshot) return d;
      return {
        ...d,
        tables: snapshotOriginalTables(d._originalTablesSnapshot),
        _hasEdits: false
      };
    }));
    undoStackRef.current = undoStackRef.current.filter(e => e.docId !== selectedDoc.id);
    setSuccessMessage('All edits reset to original extraction');
    setTimeout(() => setSuccessMessage(null), 3000);
  }, [selectedDoc]);

  // =========================================================================
  // Derived UI helpers
  // =========================================================================

  const docStatus = selectedDoc ? STATUS_CONFIG[selectedDoc.status] : null;
  const hasUndo = undoStackRef.current.length > 0;

  // The tables array for the currently-viewed doc
  const visibleTables = selectedDoc?.tables || [];

  // Ensure activeTabIdx stays in range when tables change
  useEffect(() => {
    if (activeTabIdx >= visibleTables.length) setActiveTabIdx(-1);
  }, [visibleTables.length, activeTabIdx]);

  // =========================================================================
  // Render
  // =========================================================================

  return (
    <div className="sf-root h-screen flex flex-col bg-white overflow-hidden">
      {/* Global styles: Inter font, custom animations, scrollbar */}
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700&display=swap');
        .sf-root, .sf-root input, .sf-root button, .sf-root select, .sf-root textarea {
          font-family: 'Inter', Arial, sans-serif;
          font-feature-settings: 'cv02','cv03','cv04','cv11';
        }
        @keyframes sf-shimmer {
          0% { background-position: 200% 0; }
          100% { background-position: -200% 0; }
        }
        @keyframes sf-slide-in-right {
          from { transform: translateX(100%); opacity: 0; }
          to { transform: translateX(0); opacity: 1; }
        }
        @keyframes sf-fade-in {
          from { opacity: 0; transform: translateY(-4px); }
          to { opacity: 1; transform: translateY(0); }
        }
        .sf-slide-right { animation: sf-slide-in-right 0.25s ease-out; }
        .sf-fade-in { animation: sf-fade-in 0.15s ease-out; }
        /* Scrollbar */
        .sf-root ::-webkit-scrollbar { width: 8px; height: 8px; }
        .sf-root ::-webkit-scrollbar-thumb { background: #D1D5DB; border-radius: 4px; }
        .sf-root ::-webkit-scrollbar-thumb:hover { background: #9CA3AF; }
        .sf-root ::-webkit-scrollbar-track { background: transparent; }
      `}</style>

      {/* Modals */}
      <TallySettingsModal
        isOpen={showTallySettings}
        onClose={() => setShowTallySettings(false)}
        settings={companySettings}
        onSave={setCompanySettings}
      />

      {/* ============================= TOP BAR ============================= */}
      <header className="flex-shrink-0 bg-white border-b border-[#E5E7EB] z-30">
        <div className="h-14 px-6 flex items-center justify-between">
          {/* Left: mobile menu + logo + app name */}
          <div className="flex items-center gap-3 min-w-0">
            <button
              onClick={() => setMobileActiveTab('docs')}
              className="md:hidden p-2 -ml-2 text-[#4B5563] hover:bg-[#F9FAFB] rounded-md"
              aria-label="Open document list"
            >
              <Menu className="w-5 h-5" />
            </button>
            <div className="flex items-center gap-2.5 min-w-0">
              <div className="w-9 h-9 bg-[#EA580C] rounded-lg flex items-center justify-center flex-shrink-0">
                <FileText className="w-5 h-5 text-white" />
              </div>
              <div className="min-w-0 hidden sm:block">
                <div className="text-[16px] font-semibold leading-tight"><span className="text-[#111827]">Sypro</span><span className="text-[#EA580C]">Flow</span></div>
              </div>
            </div>
          </div>

          {/* Center: document name + status pill */}
          <div className="flex-1 flex items-center justify-center gap-2 px-3 min-w-0">
            {selectedDoc && (
              <>
                <span className="text-[14px] font-medium text-[#111827] truncate max-w-[40vw] md:max-w-none" title={selectedDoc.name}>
                  {truncateFileName(selectedDoc.name, 40)}
                </span>
                {docStatus && (
                  <span className={`flex-shrink-0 inline-flex items-center gap-1 px-[10px] py-[2px] rounded-full text-[11px] font-medium ${docStatus.bg} ${docStatus.text}`}>
                    {selectedDoc.status === 'PROCESSING' && <Loader2 className="w-3 h-3 animate-spin" />}
                    {docStatus.label}
                  </span>
                )}
              </>
            )}
          </div>

          {/* Right: Settings gear + user menu */}
          <div className="flex items-center gap-1">
            <button
              onClick={() => setShowSettingsDrawer(true)}
              className="inline-flex items-center gap-1.5 px-2 md:px-3 py-1.5 text-[13px] font-medium text-[#4B5563] hover:bg-[#F9FAFB] rounded-md transition-colors"
              aria-label="Settings"
            >
              <SettingsIcon className="w-4 h-4" />
              <span className="hidden md:inline">Settings</span>
            </button>
            <div className="relative user-menu-container">
              <button
                onClick={() => setShowUserMenu(!showUserMenu)}
                className="inline-flex items-center gap-1 px-2 py-1.5 text-[13px] font-medium text-[#4B5563] hover:bg-[#F9FAFB] rounded-md transition-colors"
                aria-label="User menu"
              >
                <div className="w-7 h-7 rounded-full bg-[#FED7AA] text-[#EA580C] flex items-center justify-center text-[12px] font-semibold">
                  {userEmail ? userEmail[0].toUpperCase() : <User className="w-3.5 h-3.5" />}
                </div>
                <ChevronDown className={`hidden md:block w-3 h-3 transition-transform ${showUserMenu ? 'rotate-180' : ''}`} />
              </button>
              {showUserMenu && (
                <div className="absolute right-0 top-full mt-1 w-64 bg-white border border-[#E5E7EB] rounded-lg shadow-lg py-1 sf-fade-in z-50">
                  <div className="px-3 py-2 border-b border-[#F3F4F6]">
                    <div className="text-[11px] text-[#9CA3AF] uppercase tracking-wide">Signed in as</div>
                    <div className="text-[13px] font-medium text-[#111827] truncate">{userEmail || '—'}</div>
                  </div>
                  <button
                    onClick={() => { setShowUserMenu(false); setShowSettingsDrawer(true); }}
                    className="w-full px-3 py-2 text-left text-[13px] text-[#111827] hover:bg-[#F9FAFB] flex items-center gap-2"
                  >
                    <User className="w-4 h-4 text-[#9CA3AF]" /> Account &amp; Settings
                  </button>
                  <button
                    onClick={() => { setShowUserMenu(false); window.open('https://syproflow.com/support', '_blank'); }}
                    className="w-full px-3 py-2 text-left text-[13px] text-[#111827] hover:bg-[#F9FAFB] flex items-center gap-2"
                  >
                    <HelpCircle className="w-4 h-4 text-[#9CA3AF]" /> Support
                  </button>
                  <div className="my-1 border-t border-[#F3F4F6]" />
                  <button
                    onClick={() => {
                      if (window.confirm('Sign out of SyproFlow?')) handleAppLogout();
                    }}
                    className="w-full px-3 py-2 text-left text-[13px] text-[#EF4444] hover:bg-red-50 flex items-center gap-2"
                  >
                    <LogOut className="w-4 h-4" /> Sign Out
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      </header>

      {/* Notification strip (error / success / batch progress) */}
      <div className="flex-shrink-0 pointer-events-none">
        <div className="pointer-events-auto">
          {batchMode && batchStatus === 'processing' && batchProgress && (
            <div className="mx-3 md:mx-5 mt-2 px-3 py-2 bg-orange-50 border border-[#FED7AA] rounded-md flex items-center gap-3">
              <Loader2 className="w-4 h-4 text-[#EA580C] animate-spin flex-shrink-0" />
              <div className="flex-1 min-w-0">
                <div className="text-[12px] font-medium text-[#B45309]">
                  Batch: {batchProgress.completed_docs}/{batchProgress.total_docs} documents
                </div>
                <div className="w-full bg-[#FED7AA] rounded-full h-1 mt-1 overflow-hidden">
                  <div className="h-full bg-[#EA580C] transition-all" style={{ width: `${batchProgress.percentage || 0}%` }} />
                </div>
              </div>
              <span className="text-[11px] text-[#B45309] tabular-nums">{batchProgress.elapsed_time || 0}s</span>
            </div>
          )}
          {error && (
            <div className="mx-3 md:mx-5 mt-2 px-3 py-2 bg-red-50 border border-red-200 rounded-md flex items-center justify-between">
              <div className="flex items-center gap-2 min-w-0">
                <AlertCircle className="w-4 h-4 text-[#EF4444] flex-shrink-0" />
                <span className="text-[12px] text-red-700 truncate">{error}</span>
              </div>
              <button onClick={() => setError(null)} className="p-0.5 text-red-400 hover:text-red-600 flex-shrink-0">
                <X className="w-3.5 h-3.5" />
              </button>
            </div>
          )}
          {successMessage && (
            <div className="mx-3 md:mx-5 mt-2 px-3 py-2 bg-emerald-50 border border-emerald-200 rounded-md flex items-center gap-2">
              <CheckCircle className="w-4 h-4 text-emerald-500 flex-shrink-0" />
              <span className="text-[12px] text-emerald-700">{successMessage}</span>
            </div>
          )}
          {standardLimitWarning && (
            <div className="mx-3 md:mx-5 mt-2 px-3 py-2 bg-orange-50 border border-[#FED7AA] rounded-md">
              <div className="flex items-center justify-between gap-2">
                <span className="text-[12px] text-[#B45309] flex-1">{standardLimitWarning}</span>
                <div className="flex gap-1">
                  <button
                    onClick={() => { setModelTier('professional'); setStandardLimitWarning(null); }}
                    className="px-2 py-1 bg-[#EA580C] text-white rounded text-[11px] font-semibold hover:bg-[#C2410C]"
                  >
                    Switch to Pro
                  </button>
                  <button onClick={() => setStandardLimitWarning(null)} className="p-1 text-[#B45309]">
                    <X className="w-3.5 h-3.5" />
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* ============================= MAIN AREA ============================= */}
      {/* Desktop (md+): three-column workbench. Mobile (<md): tabbed single column. */}
      <main className="flex-1 flex min-h-0 overflow-hidden">

        {/* ----------------- DESKTOP: SIDEBAR ----------------- */}
        <aside
          className={`hidden md:flex flex-shrink-0 flex-col border-r border-[#E5E7EB] bg-[#F9FAFB] transition-[width] duration-200 min-h-0 ${
            sidebarCollapsed ? 'w-[44px]' : 'w-[220px] xl:w-[240px]'
          }`}
        >
          {/* Sidebar header */}
          <div className={`flex items-center ${sidebarCollapsed ? 'justify-center' : 'justify-between'} px-2 py-2 border-b border-[#E5E7EB]`}>
            {!sidebarCollapsed && (
              <span className="text-[11px] font-semibold text-[#9CA3AF] uppercase tracking-wide">Documents</span>
            )}
            <button
              onClick={() => setSidebarCollapsed(!sidebarCollapsed)}
              className="p-1 text-[#9CA3AF] hover:text-[#4B5563] hover:bg-white rounded transition-colors"
              title={sidebarCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
            >
              {sidebarCollapsed ? <PanelLeftOpen className="w-4 h-4" /> : <PanelLeftClose className="w-4 h-4" />}
            </button>
          </div>

          {/* Collapsed state: icon strip */}
          {sidebarCollapsed ? (
            <div className="flex-1 overflow-y-auto py-1">
              {documents.map(doc => {
                const isSelected = selectedDocId === doc.id;
                const st = STATUS_CONFIG[doc.status];
                return (
                  <button
                    key={doc.id}
                    onClick={() => setSelectedDocId(doc.id)}
                    title={doc.name}
                    className={`relative w-full flex items-center justify-center py-2 my-0.5 transition-colors ${
                      isSelected ? 'bg-[#FFF7ED] border-l-2 border-[#EA580C]' : 'hover:bg-white border-l-2 border-transparent'
                    }`}
                  >
                    {doc.type?.includes('pdf') || doc.name?.toLowerCase().endsWith('.pdf')
                      ? <FileText className={`w-4 h-4 ${isSelected ? 'text-[#EA580C]' : 'text-[#4B5563]'}`} />
                      : <ImageIcon className={`w-4 h-4 ${isSelected ? 'text-[#EA580C]' : 'text-[#4B5563]'}`} />}
                    <span className={`absolute bottom-1 right-1 w-1.5 h-1.5 rounded-full ${st.dot}`} />
                  </button>
                );
              })}
              <button
                onClick={() => sidebarFileInputRef.current?.click()}
                className="w-full flex items-center justify-center py-2 my-0.5 text-[#9CA3AF] hover:text-[#EA580C] hover:bg-white transition-colors"
                title="Upload another document"
              >
                <Plus className="w-4 h-4" />
              </button>
            </div>
          ) : (
            /* Expanded state */
            <div className="flex-1 flex flex-col min-h-0">
              {documents.length === 0 ? (
                /* Landing state: full-width dropzone */
                <div
                  className="flex-1 flex items-center justify-center p-3"
                  onDrop={handleDrop}
                  onDragOver={handleDragOver}
                  onDragLeave={handleDragLeave}
                >
                  <div
                    onClick={() => sidebarFileInputRef.current?.click()}
                    className={`w-full h-full min-h-[200px] flex flex-col items-center justify-center text-center border-2 border-dashed rounded-lg cursor-pointer transition-colors ${
                      isDragging
                        ? 'border-[#EA580C] bg-[#FFF7ED]'
                        : 'border-[#E5E7EB] hover:border-[#EA580C] hover:bg-white'
                    }`}
                  >
                    <Upload className={`w-8 h-8 mb-2 ${isDragging ? 'text-[#EA580C]' : 'text-[#9CA3AF]'}`} />
                    <p className="text-[13px] font-semibold text-[#111827]">
                      {isDragging ? 'Drop to upload' : 'Drop a document'}
                    </p>
                    <p className="text-[11px] text-[#9CA3AF] mt-1 px-3">
                      or <span className="text-[#EA580C] font-medium">browse</span>
                    </p>
                    <p className="text-[10px] text-[#9CA3AF] mt-3 px-3">
                      PDF, JPG, PNG — up to 50 pages
                    </p>
                  </div>
                </div>
              ) : (
                /* Document list */
                <>
                  <div className="flex-1 overflow-y-auto p-1.5 space-y-0.5">
                    {documents.map(doc => {
                      const st = STATUS_CONFIG[doc.status];
                      const isSelected = selectedDocId === doc.id;
                      return (
                        <div
                          key={doc.id}
                          onClick={() => setSelectedDocId(doc.id)}
                          className={`group relative px-2 py-2 rounded-md cursor-pointer transition-colors ${
                            isSelected
                              ? 'bg-white border border-[#E5E7EB] shadow-sm'
                              : 'hover:bg-white/60 border border-transparent'
                          }`}
                        >
                          <div className="flex items-start gap-2">
                            {isSelected && <span className="absolute left-0 top-0 bottom-0 w-0.5 bg-[#EA580C] rounded-r" />}
                            <div className="flex-shrink-0 mt-0.5">
                              {doc.type?.includes('pdf') || doc.name?.toLowerCase().endsWith('.pdf')
                                ? <FileText className={`w-4 h-4 ${isSelected ? 'text-[#EA580C]' : 'text-[#9CA3AF]'}`} />
                                : <ImageIcon className={`w-4 h-4 ${isSelected ? 'text-[#EA580C]' : 'text-[#9CA3AF]'}`} />}
                            </div>
                            <div className="flex-1 min-w-0">
                              <div className={`text-[13px] font-medium truncate ${isSelected ? 'text-[#111827]' : 'text-[#4B5563]'}`} title={doc.name}>
                                {truncateFileName(doc.name, 26)}
                              </div>
                              <div className="flex items-center gap-1.5 mt-0.5">
                                <span className={`inline-flex items-center gap-1 text-[10px] ${st.text}`}>
                                  <span className={`w-1.5 h-1.5 rounded-full ${st.dot}`} />
                                  {st.label}
                                </span>
                                <span className="text-[10px] text-[#9CA3AF]">·</span>
                                <span className="text-[10px] text-[#9CA3AF]">{formatFileSize(doc.size)}</span>
                              </div>
                              {doc.status === 'PROCESSING' && currentProgress && selectedDocId === doc.id && (
                                <div className="mt-1.5">
                                  <ProgressBar progress={currentProgress} />
                                </div>
                              )}
                            </div>
                            <button
                              onClick={(e) => { e.stopPropagation(); deleteDocument(doc.id); }}
                              className="p-1 text-[#9CA3AF] hover:text-[#EF4444] opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0"
                              title="Remove from session"
                            >
                              <X className="w-3.5 h-3.5" />
                            </button>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                  {/* "+ Upload another" */}
                  <button
                    onClick={() => sidebarFileInputRef.current?.click()}
                    className="m-1.5 flex items-center justify-center gap-1.5 py-2 text-[12px] font-medium text-[#4B5563] bg-white border border-[#E5E7EB] rounded-md hover:border-[#EA580C] hover:text-[#EA580C] transition-colors"
                  >
                    <Plus className="w-3.5 h-3.5" />
                    Upload another
                  </button>
                </>
              )}
            </div>
          )}
        </aside>

        {/* ------- MOBILE: tab bar (Source | Data | Docs) ------- */}
        {selectedDoc && (
          <div className="md:hidden flex-shrink-0 flex items-center border-b border-[#E5E7EB] bg-white">
            {[
              { id: 'source', label: 'Source' },
              { id: 'data', label: 'Data' },
              { id: 'docs', label: 'Docs' }
            ].map(tab => (
              <button
                key={tab.id}
                onClick={() => setMobileActiveTab(tab.id)}
                className={`flex-1 py-2.5 text-[13px] font-medium transition-colors ${
                  mobileActiveTab === tab.id
                    ? 'text-[#EA580C] border-b-2 border-[#EA580C]'
                    : 'text-[#4B5563] border-b-2 border-transparent'
                }`}
              >
                {tab.label}
              </button>
            ))}
          </div>
        )}

        {/* ----------------- MAIN WORKBENCH ----------------- */}
        <div className="flex-1 flex flex-col min-w-0 min-h-0">
          {!selectedDoc ? (
            /* Landing: no document selected */
            <div className="flex-1 flex items-center justify-center p-6 bg-[#F9FAFB]">
              <div className="text-center max-w-md">
                <div className="w-16 h-16 mx-auto mb-4 bg-[#FED7AA] rounded-2xl flex items-center justify-center">
                  <Upload className="w-8 h-8 text-[#EA580C]" />
                </div>
                <h2 className="text-[20px] font-semibold text-[#111827] mb-1">
                  Welcome to SyproFlow
                </h2>
                <p className="text-[14px] text-[#4B5563] mb-4">
                  Extract structured data from any document in seconds. Powered by AI.
                </p>
                <button
                  onClick={() => sidebarFileInputRef.current?.click()}
                  className="inline-flex items-center gap-2 px-5 py-2.5 bg-[#EA580C] hover:bg-[#C2410C] text-white rounded-md text-[14px] font-semibold shadow-sm transition-colors"
                >
                  <Upload className="w-4 h-4" />
                  Upload your document
                </button>
                {/* Mobile-only: Camera + Files shortcut */}
                <div className="md:hidden flex gap-2 mt-3 justify-center">
                  <button
                    onClick={() => cameraInputRef.current?.click()}
                    className="inline-flex items-center gap-1.5 px-3 py-2 bg-white border border-[#E5E7EB] text-[#4B5563] rounded-md text-[13px] font-medium"
                  >
                    <Camera className="w-4 h-4" /> Camera
                  </button>
                  <button
                    onClick={() => fileInputRef.current?.click()}
                    className="inline-flex items-center gap-1.5 px-3 py-2 bg-white border border-[#E5E7EB] text-[#4B5563] rounded-md text-[13px] font-medium"
                  >
                    <FolderOpen className="w-4 h-4" /> Files
                  </button>
                </div>
                <p className="text-[11px] text-[#9CA3AF] mt-4">
                  PDF, JPG, PNG — up to 50 pages. Your data is never stored or used for AI training.
                </p>
                <p className="text-[11px] text-[#9CA3AF] mt-2">
                  Need the Tally Bridge?{' '}
                  <button
                    onClick={() => navigate('/download')}
                    className="text-[#EA580C] hover:underline font-medium bg-transparent border-none p-0 cursor-pointer text-[11px]"
                  >
                    Download it
                  </button>
                </p>
              </div>
            </div>
          ) : (
            <>
              {/* =============== DESKTOP SPLIT LAYOUT =============== */}
              <div className="hidden md:flex flex-1 min-h-0">
                {/* Source preview column */}
                {showSourcePanel && selectedDoc.file && (
                  <div className="flex-1 flex flex-col min-w-0 border-r border-[#E5E7EB]">
                    <SourcePreview file={selectedDoc.file} zoom={sourceZoom} onZoomChange={setSourceZoom} />
                  </div>
                )}
                {/* Data panel column */}
                <div className={`flex flex-col min-w-0 min-h-0 ${showSourcePanel && selectedDoc.file ? 'flex-1' : 'flex-1'}`}>
                  <DataPanelContent
                    selectedDoc={selectedDoc}
                    activeTabIdx={activeTabIdx}
                    setActiveTabIdx={setActiveTabIdx}
                    visibleTables={visibleTables}
                    excludedFields={excludedFields}
                    toggleFieldExclusion={toggleFieldExclusion}
                    editingCell={editingCell}
                    editValue={editValue}
                    setEditValue={setEditValue}
                    startCellEdit={startCellEdit}
                    saveCellEdit={saveCellEdit}
                    cancelCellEdit={cancelCellEdit}
                    deleteRow={deleteRow}
                    duplicateRow={duplicateRow}
                    currentProgress={currentProgress}
                    extractSingle={extractSingle}
                    getPageSelection={getPageSelection}
                    updatePageSelection={updatePageSelection}
                    parsePageCount={parsePageCount}
                    isSearching={isSearching}
                    searchKeyword={searchKeyword}
                    togglePageFromSearch={togglePageFromSearch}
                    toggleAllSearchResults={toggleAllSearchResults}
                    updateDocPassword={updateDocPassword}
                    batchMode={batchMode}
                    batchId={batchId}
                    batchStatus={batchStatus}
                    documents={documents}
                    startBatchProcessing={startBatchProcessing}
                    pilotQuota={pilotQuota}
                  />
                </div>
              </div>

              {/* =============== MOBILE TAB CONTENT =============== */}
              <div className="md:hidden flex-1 flex flex-col min-h-0">
                {mobileActiveTab === 'source' && (
                  <div className="flex-1 flex flex-col min-h-0">
                    {selectedDoc.file
                      ? <SourcePreview file={selectedDoc.file} zoom={sourceZoom} onZoomChange={setSourceZoom} />
                      : <div className="flex-1 flex items-center justify-center text-[#9CA3AF] text-[13px]">No source file</div>}
                  </div>
                )}
                {mobileActiveTab === 'data' && (
                  <DataPanelContent
                    selectedDoc={selectedDoc}
                    activeTabIdx={activeTabIdx}
                    setActiveTabIdx={setActiveTabIdx}
                    visibleTables={visibleTables}
                    excludedFields={excludedFields}
                    toggleFieldExclusion={toggleFieldExclusion}
                    editingCell={editingCell}
                    editValue={editValue}
                    setEditValue={setEditValue}
                    startCellEdit={startCellEdit}
                    saveCellEdit={saveCellEdit}
                    cancelCellEdit={cancelCellEdit}
                    deleteRow={deleteRow}
                    duplicateRow={duplicateRow}
                    currentProgress={currentProgress}
                    extractSingle={extractSingle}
                    getPageSelection={getPageSelection}
                    updatePageSelection={updatePageSelection}
                    parsePageCount={parsePageCount}
                    isSearching={isSearching}
                    searchKeyword={searchKeyword}
                    togglePageFromSearch={togglePageFromSearch}
                    toggleAllSearchResults={toggleAllSearchResults}
                    updateDocPassword={updateDocPassword}
                    batchMode={batchMode}
                    batchId={batchId}
                    batchStatus={batchStatus}
                    documents={documents}
                    startBatchProcessing={startBatchProcessing}
                    pilotQuota={pilotQuota}
                  />
                )}
                {mobileActiveTab === 'docs' && (
                  <MobileDocsList
                    documents={documents}
                    selectedDocId={selectedDocId}
                    onSelect={(id) => { setSelectedDocId(id); setMobileActiveTab('data'); }}
                    onDelete={deleteDocument}
                    onUploadCamera={() => cameraInputRef.current?.click()}
                    onUploadFiles={() => fileInputRef.current?.click()}
                    currentProgress={currentProgress}
                  />
                )}
              </div>

              {/* =============== BOTTOM ACTION BAR =============== */}
              <BottomActionBar
                selectedDoc={selectedDoc}
                showSourcePanel={showSourcePanel}
                setShowSourcePanel={setShowSourcePanel}
                onReextract={reextractWithTier}
                onReset={resetAllEdits}
                currentTier={modelTier}
                showReextractDropdown={showReextractDropdown}
                setShowReextractDropdown={setShowReextractDropdown}
                showExportDropdown={showExportDropdown}
                setShowExportDropdown={setShowExportDropdown}
                handleQuickExport={handleQuickExport}
                tallyEnabled={companySettings.tallyEnabled}
                onPreviewAndPush={handlePreviewAndPush}
                voucherTypeOverrides={voucherTypeOverrides}
                setVoucherTypeOverrides={setVoucherTypeOverrides}
                hasGstin={!!companySettings.gstin}
                tallyBridgeStatus={tallyBridgeStatus}
                hasUndo={hasUndo}
                onUndo={undoLastEdit}
                extractAll={extractAll}
                isExtracting={isExtracting}
                pendingCount={documents.filter(d => d.status === 'PENDING').length}
              />
            </>
          )}
        </div>
      </main>

      {/* Hidden file inputs (critical: multiple= for batch, capture= for mobile camera) */}
      <input
        ref={fileInputRef}
        type="file"
        multiple
        accept=".pdf,.jpg,.jpeg,.png,.webp"
        onChange={handleFileSelect}
        className="hidden"
      />
      <input
        ref={cameraInputRef}
        type="file"
        accept="image/*"
        capture="environment"
        onChange={handleFileSelect}
        className="hidden"
      />
      <input
        ref={sidebarFileInputRef}
        type="file"
        multiple
        accept=".pdf,.jpg,.jpeg,.png,.webp"
        onChange={handleFileSelect}
        className="hidden"
      />

      {/* Settings drawer (slide in from right) */}
      <SettingsDrawer
        isOpen={showSettingsDrawer}
        onClose={() => setShowSettingsDrawer(false)}
        extractionMode={extractionMode}
        setExtractionMode={setExtractionMode}
        modelTier={modelTier}
        setModelTier={setModelTier}
        companySettings={companySettings}
        setCompanySettings={setCompanySettings}
        onOpenTallySettings={() => setShowTallySettings(true)}
        tallyBridgeStatus={tallyBridgeStatus}
        onLogout={handleAppLogout}
        userEmail={userEmail}
      />

      {/* Admin overlay & chat widget */}
      {showAdmin && <AdminDashboard onClose={() => setShowAdmin(false)} />}
      {/* Chat widget hidden per UI redesign v1.0 — will be reintroduced post-launch */}
      {/* <ChatWidget /> */}

      {/* Footer (minimal — keeps the legal line for now) */}
      <footer className="hidden md:block flex-shrink-0 border-t border-[#E5E7EB] bg-white py-1.5">
        <div className="px-5 text-center text-[10px] text-[#9CA3AF]">
          © 2026 <span className="font-medium text-[#4B5563]">SyproOne Solutions Pvt Ltd</span>. All rights reserved.
        </div>
      </footer>
    </div>
  );
}

// =============================================================================
// DataPanelContent — renders the tab bar + tab body (Summary or a TableTab)
// Also handles the PENDING / PROCESSING / FAILED states.
// =============================================================================

function DataPanelContent({
  selectedDoc, activeTabIdx, setActiveTabIdx,
  visibleTables, excludedFields, toggleFieldExclusion,
  editingCell, editValue, setEditValue,
  startCellEdit, saveCellEdit, cancelCellEdit,
  deleteRow, duplicateRow,
  currentProgress, extractSingle,
  getPageSelection, updatePageSelection, parsePageCount,
  isSearching, searchKeyword, togglePageFromSearch, toggleAllSearchResults,
  updateDocPassword,
  batchMode, batchId, batchStatus,
  documents, startBatchProcessing,
  pilotQuota
}) {
  if (selectedDoc.status === 'PROCESSING') {
    return (
      <div className="flex-1 flex items-center justify-center">
        <div className="text-center max-w-xs px-4">
          <Loader2 className="w-10 h-10 text-[#EA580C] animate-spin mx-auto mb-3" />
          <p className="text-[14px] font-semibold text-[#111827] mb-1">Extracting data</p>
          <p className="text-[12px] text-[#4B5563] mb-3">Our AI is reading your document.</p>
          {currentProgress && <ProgressBar progress={currentProgress} />}
        </div>
      </div>
    );
  }

  if (selectedDoc.status === 'FAILED') {
    return (
      <div className="flex-1 flex items-center justify-center">
        <div className="text-center">
          <AlertCircle className="w-12 h-12 text-[#EF4444] mx-auto mb-3" />
          <p className="text-[14px] font-semibold text-[#111827] mb-1">Extraction failed</p>
          <p className="text-[12px] text-[#4B5563] mb-4">Try again or switch to a higher-accuracy tier.</p>
          <button
            onClick={() => extractSingle(selectedDoc.id)}
            className="px-4 py-2 bg-[#EA580C] hover:bg-[#C2410C] text-white rounded-md text-[13px] font-semibold"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  if (selectedDoc.status === 'PENDING') {
    // Extraction options panel (password + page selection + extract button)
    const selection = getPageSelection(selectedDoc.id);
    const isPDF = selectedDoc.name?.toLowerCase().endsWith('.pdf');
    const extractLabel = selection.mode === 'specific' && selection.pages
      ? `Extract ${parsePageCount(selection.pages)} Page(s)`
      : selection.mode === 'keyword' && (selection.selectedPages || []).length > 0
      ? `Extract ${(selection.selectedPages || []).length} Page(s)`
      : 'Extract All Pages';
    const disabled = selection.mode === 'keyword' && (selection.selectedPages || []).length === 0;

    return (
      <div className="flex-1 overflow-auto">
        <div className="max-w-2xl mx-auto p-6">
          <div className="text-center mb-5">
            <div className="w-14 h-14 mx-auto mb-3 bg-[#FED7AA] rounded-xl flex items-center justify-center">
              <FileSpreadsheet className="w-7 h-7 text-[#EA580C]" />
            </div>
            <h2 className="text-[20px] font-semibold text-[#111827]">Ready to extract</h2>
            <p className="text-[13px] text-[#4B5563] mt-1">Choose options below and press <span className="font-semibold">Extract</span>.</p>
          </div>

          {/* Password for protected PDFs */}
          {isPDF && (
            <div className="mb-4 p-4 bg-orange-50 rounded-lg border border-[#FED7AA]">
              <div className="flex items-center gap-2 mb-2">
                <Lock className="w-4 h-4 text-[#EA580C]" />
                <h4 className="text-[13px] font-semibold text-[#B45309]">Password-protected PDF?</h4>
              </div>
              <p className="text-[12px] text-[#B45309] mb-2">Enter the password if required. Leave blank otherwise.</p>
              <input
                type="password"
                value={selectedDoc.password || ''}
                onChange={(e) => updateDocPassword(selectedDoc.id, e.target.value)}
                placeholder="PDF password"
                className="w-full px-3 py-2 text-[13px] border border-[#FED7AA] rounded-md bg-white focus:outline-none focus:border-[#EA580C]"
              />
            </div>
          )}

          {/* Page selection */}
          <div className="mb-4 p-4 bg-[#F9FAFB] rounded-lg border border-[#E5E7EB]">
            <h4 className="text-[13px] font-semibold text-[#111827] mb-3">Page Selection</h4>
            <div className="space-y-2.5">
              <label className="flex items-center gap-2.5 cursor-pointer">
                <input
                  type="radio"
                  name={`pageMode-${selectedDoc.id}`}
                  checked={selection.mode === 'all'}
                  onChange={() => updatePageSelection(selectedDoc.id, { mode: 'all' })}
                  className="w-4 h-4 accent-[#EA580C]"
                />
                <span className="text-[13px] text-[#111827]">All pages</span>
              </label>
              <label className="flex items-center gap-2.5 cursor-pointer">
                <input
                  type="radio"
                  name={`pageMode-${selectedDoc.id}`}
                  checked={selection.mode === 'specific'}
                  onChange={() => updatePageSelection(selectedDoc.id, { mode: 'specific' })}
                  className="w-4 h-4 accent-[#EA580C]"
                />
                <span className="text-[13px] text-[#111827]">Specific pages</span>
              </label>
              {selection.mode === 'specific' && (
                <input
                  type="text"
                  value={selection.pages || ''}
                  onChange={(e) => updatePageSelection(selectedDoc.id, { pages: e.target.value })}
                  placeholder="e.g., 1-3, 5, 7-10"
                  className="w-full ml-6 px-3 py-2 text-[13px] border border-[#E5E7EB] rounded-md focus:outline-none focus:border-[#EA580C]"
                  style={{ width: 'calc(100% - 1.5rem)' }}
                />
              )}
              <label className="flex items-center gap-2.5 cursor-pointer">
                <input
                  type="radio"
                  name={`pageMode-${selectedDoc.id}`}
                  checked={selection.mode === 'keyword'}
                  onChange={() => updatePageSelection(selectedDoc.id, { mode: 'keyword' })}
                  className="w-4 h-4 accent-[#EA580C]"
                />
                <span className="text-[13px] text-[#111827]">Search by keyword</span>
              </label>
              {selection.mode === 'keyword' && (
                <div className="ml-6 space-y-2">
                  <div className="flex gap-2">
                    <input
                      type="text"
                      value={selection.keyword || ''}
                      onChange={(e) => updatePageSelection(selectedDoc.id, { keyword: e.target.value })}
                      placeholder="Keyword"
                      className="flex-1 px-3 py-2 text-[13px] border border-[#E5E7EB] rounded-md focus:outline-none focus:border-[#EA580C]"
                    />
                    <button
                      onClick={() => searchKeyword(selectedDoc.id)}
                      disabled={isSearching || !selection.keyword?.trim()}
                      className="px-3 py-2 bg-[#EA580C] hover:bg-[#C2410C] text-white rounded-md text-[13px] font-semibold disabled:opacity-50 flex items-center gap-1.5"
                    >
                      {isSearching ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Search className="w-3.5 h-3.5" />}
                    </button>
                  </div>
                  {selection.searchResults && (
                    <div className="p-3 bg-white border border-[#E5E7EB] rounded-md">
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-[12px] font-semibold text-[#4B5563]">
                          Found on {selection.searchResults.page_count} page(s)
                        </span>
                        {selection.searchResults.page_count > 0 && (
                          <div className="flex gap-2">
                            <button onClick={() => toggleAllSearchResults(selectedDoc.id, true)} className="text-[11px] text-[#EA580C] hover:underline">Select All</button>
                            <button onClick={() => toggleAllSearchResults(selectedDoc.id, false)} className="text-[11px] text-[#9CA3AF] hover:underline">Clear</button>
                          </div>
                        )}
                      </div>
                      <div className="max-h-40 overflow-y-auto space-y-1">
                        {selection.searchResults.matches?.map((match, idx) => {
                          const isSel = (selection.selectedPages || []).includes(match.page);
                          return (
                            <label
                              key={idx}
                              className={`flex items-start gap-2 p-2 rounded cursor-pointer ${
                                isSel ? 'bg-[#FFF7ED] border border-[#FED7AA]' : 'hover:bg-[#F9FAFB]'
                              }`}
                            >
                              <input
                                type="checkbox"
                                checked={isSel}
                                onChange={() => togglePageFromSearch(selectedDoc.id, match.page)}
                                className="w-4 h-4 mt-0.5 accent-[#EA580C]"
                              />
                              <div className="flex-1 min-w-0">
                                <div className="text-[12px] font-semibold text-[#111827]">Page {match.page}</div>
                                <div className="text-[11px] text-[#9CA3AF] truncate">{match.snippet}</div>
                              </div>
                            </label>
                          );
                        })}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>

          {batchMode ? (
            <div className="p-4 bg-orange-50 border border-[#FED7AA] rounded-lg text-center">
              <p className="text-[13px] font-semibold text-[#B45309] mb-1">Batch mode active</p>
              <p className="text-[12px] text-[#B45309]">
                This document is queued.{' '}
                {documents.filter(d => d.batchId === batchId && d.status === 'PENDING').length} in queue.
              </p>
              <button
                onClick={startBatchProcessing}
                className="mt-3 px-4 py-2 bg-[#EA580C] hover:bg-[#C2410C] text-white rounded-md text-[13px] font-semibold"
              >
                Start batch processing
              </button>
            </div>
          ) : (
            <button
              onClick={() => extractSingle(selectedDoc.id)}
              disabled={disabled}
              className="w-full px-5 py-3 bg-[#EA580C] hover:bg-[#C2410C] text-white rounded-md text-[14px] font-semibold shadow-sm transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {extractLabel}
            </button>
          )}
        </div>
      </div>
    );
  }

  // COMPLETED: tabs
  return (
    <div className="flex-1 flex flex-col min-h-0">
      {/* Tab bar */}
      <div className="flex-shrink-0 flex items-center overflow-x-auto border-b border-[#E5E7EB] bg-white">
        <button
          onClick={() => setActiveTabIdx(-1)}
          className={`flex-shrink-0 px-4 py-2.5 text-[13px] font-medium transition-colors ${
            activeTabIdx === -1
              ? 'text-[#EA580C] border-b-2 border-[#EA580C]'
              : 'text-[#4B5563] border-b-2 border-transparent hover:text-[#111827]'
          }`}
        >
          Summary
        </button>
        {visibleTables.map((table, idx) => (
          <button
            key={idx}
            onClick={() => setActiveTabIdx(idx)}
            className={`flex-shrink-0 px-4 py-2.5 text-[13px] font-medium transition-colors ${
              activeTabIdx === idx
                ? 'text-[#EA580C] border-b-2 border-[#EA580C]'
                : 'text-[#4B5563] border-b-2 border-transparent hover:text-[#111827]'
            }`}
          >
            {table.title || table.name || `Table ${idx + 1}`}
            <span className="ml-1.5 text-[11px] text-[#9CA3AF] tabular-nums">
              ({(table.rows || table.data || []).length})
            </span>
          </button>
        ))}
      </div>

      {/* Tab body */}
      <div className="flex-1 flex flex-col min-h-0 bg-white">
        {activeTabIdx === -1 ? (
          <SummaryTab doc={selectedDoc} pilotQuota={pilotQuota} />
        ) : (
          <TableTab
            doc={selectedDoc}
            table={visibleTables[activeTabIdx]}
            tableIdx={activeTabIdx}
            excludedFields={excludedFields}
            toggleFieldExclusion={toggleFieldExclusion}
            editingCell={editingCell}
            editValue={editValue}
            onStartEdit={startCellEdit}
            onChangeEdit={setEditValue}
            onCommitEdit={saveCellEdit}
            onCancelEdit={cancelCellEdit}
            onDeleteRow={deleteRow}
            onDuplicateRow={duplicateRow}
          />
        )}
      </div>
    </div>
  );
}

// =============================================================================
// MobileDocsList — full-screen docs tab for mobile
// =============================================================================

function MobileDocsList({ documents, selectedDocId, onSelect, onDelete, onUploadCamera, onUploadFiles, currentProgress }) {
  return (
    <div className="flex-1 flex flex-col min-h-0 bg-[#F9FAFB]">
      <div className="flex-shrink-0 p-3 bg-white border-b border-[#E5E7EB]">
        <div className="flex gap-2">
          <button
            onClick={onUploadCamera}
            className="flex-1 flex items-center justify-center gap-2 py-3 bg-[#FED7AA] text-[#EA580C] rounded-md text-[13px] font-semibold"
          >
            <Camera className="w-4 h-4" /> Camera
          </button>
          <button
            onClick={onUploadFiles}
            className="flex-1 flex items-center justify-center gap-2 py-3 bg-white border border-[#E5E7EB] text-[#4B5563] rounded-md text-[13px] font-semibold"
          >
            <FolderOpen className="w-4 h-4" /> Files
          </button>
        </div>
      </div>
      <div className="flex-1 overflow-y-auto p-2 space-y-1">
        {documents.length === 0 ? (
          <div className="text-center py-10">
            <FileSpreadsheet className="w-10 h-10 mx-auto mb-2 text-[#9CA3AF]" />
            <p className="text-[13px] text-[#9CA3AF]">No documents yet</p>
          </div>
        ) : documents.map(doc => {
          const st = STATUS_CONFIG[doc.status];
          const isSelected = selectedDocId === doc.id;
          return (
            <div
              key={doc.id}
              onClick={() => onSelect(doc.id)}
              className={`flex items-center gap-3 p-3 rounded-md cursor-pointer ${
                isSelected ? 'bg-white border border-[#EA580C] shadow-sm' : 'bg-white border border-[#E5E7EB]'
              }`}
            >
              {doc.type?.includes('pdf') || doc.name?.toLowerCase().endsWith('.pdf')
                ? <FileText className="w-5 h-5 text-[#4B5563] flex-shrink-0" />
                : <ImageIcon className="w-5 h-5 text-[#4B5563] flex-shrink-0" />}
              <div className="flex-1 min-w-0">
                <div className="text-[13px] font-medium text-[#111827] truncate">{doc.name}</div>
                <div className="flex items-center gap-2 mt-0.5">
                  <span className={`text-[11px] ${st.text} flex items-center gap-1`}>
                    <span className={`w-1.5 h-1.5 rounded-full ${st.dot}`} />
                    {st.label}
                  </span>
                  <span className="text-[11px] text-[#9CA3AF]">{formatFileSize(doc.size)}</span>
                </div>
                {doc.status === 'PROCESSING' && currentProgress && isSelected && (
                  <div className="mt-2"><ProgressBar progress={currentProgress} /></div>
                )}
              </div>
              <button
                onClick={(e) => { e.stopPropagation(); onDelete(doc.id); }}
                className="p-2 text-[#9CA3AF]"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// =============================================================================
// BottomActionBar — View Source | Re-extract ▾ | Export ▾ | Push to Tally
// =============================================================================

function BottomActionBar({
  selectedDoc,
  showSourcePanel, setShowSourcePanel,
  onReextract, onReset, currentTier,
  showReextractDropdown, setShowReextractDropdown,
  showExportDropdown, setShowExportDropdown,
  handleQuickExport,
  tallyEnabled, onPreviewAndPush,
  voucherTypeOverrides, setVoucherTypeOverrides, hasGstin,
  tallyBridgeStatus,
  hasUndo, onUndo,
  extractAll, isExtracting, pendingCount
}) {
  const isCompleted = selectedDoc?.status === 'COMPLETED';
  const isPending = selectedDoc?.status === 'PENDING';
  const isBankStatement = voucherTypeOverrides[selectedDoc?.id] === 'Bank Statement';
  const tiers = [
    { id: 'standard', label: 'Standard', desc: 'Fast · digital PDFs' },
    { id: 'professional', label: 'Professional', desc: 'Recommended · all docs' },
    { id: 'premium', label: 'Premium', desc: 'Max accuracy' },
  ];

  return (
    <div className="flex-shrink-0 border-t border-[#E5E7EB] bg-white">
      <div className="flex items-center justify-between gap-2 px-3 md:px-4 py-2">
        {/* Left: View Source toggle + Undo (desktop only) */}
        <div className="flex items-center gap-1.5">
          <button
            onClick={() => setShowSourcePanel(!showSourcePanel)}
            className={`hidden md:inline-flex items-center gap-1.5 px-2.5 py-1.5 text-[12px] font-medium rounded-md transition-colors ${
              showSourcePanel ? 'bg-[#FFF7ED] text-[#EA580C]' : 'text-[#4B5563] hover:bg-[#F9FAFB]'
            }`}
            title={showSourcePanel ? 'Hide source preview' : 'Show source preview'}
          >
            {showSourcePanel ? <Eye className="w-3.5 h-3.5" /> : <EyeOff className="w-3.5 h-3.5" />}
            <span className="hidden lg:inline">{showSourcePanel ? 'Hide Source' : 'View Source'}</span>
          </button>
          <button
            onClick={onUndo}
            disabled={!hasUndo}
            className="inline-flex items-center gap-1.5 px-2.5 py-1.5 text-[12px] font-medium text-[#4B5563] hover:bg-[#F9FAFB] rounded-md disabled:opacity-40 disabled:cursor-not-allowed"
            title={hasUndo ? 'Undo last change (Ctrl+Z)' : 'Nothing to undo'}
          >
            <Undo2 className="w-3.5 h-3.5" />
            <span className="hidden md:inline">Undo</span>
          </button>
        </div>

        {/* Right: Re-extract + Export + Push to Tally */}
        <div className="flex items-center gap-1.5 flex-wrap justify-end">
          {isPending && (
            <button
              onClick={extractAll}
              disabled={isExtracting || pendingCount === 0}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-[#EA580C] hover:bg-[#C2410C] text-white rounded-md text-[13px] font-semibold disabled:opacity-50 transition-colors"
            >
              {isExtracting ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />}
              <span>Extract {pendingCount > 1 ? `All (${pendingCount})` : ''}</span>
            </button>
          )}

          {isCompleted && (
            <>
              {/* Re-extract dropdown */}
              <div className="relative reextract-dropdown-container">
                <button
                  onClick={() => setShowReextractDropdown(!showReextractDropdown)}
                  className="inline-flex items-center gap-1.5 px-2.5 py-1.5 text-[12px] font-medium text-[#4B5563] bg-white border border-[#E5E7EB] hover:border-[#EA580C] hover:text-[#EA580C] rounded-md transition-colors"
                >
                  <RefreshCw className="w-3.5 h-3.5" />
                  <span className="hidden sm:inline">Re-extract</span>
                  <ChevronDown className="w-3 h-3" />
                </button>
                {showReextractDropdown && (
                  <div className="absolute right-0 bottom-full mb-1 w-60 bg-white border border-[#E5E7EB] rounded-md shadow-lg py-1 z-40 sf-fade-in">
                    <div className="px-3 py-1.5 text-[10px] font-semibold text-[#9CA3AF] uppercase tracking-wide">
                      Re-extract with
                    </div>
                    {tiers.map(tier => (
                      <button
                        key={tier.id}
                        onClick={() => { setShowReextractDropdown(false); onReextract(tier.id); }}
                        className="w-full px-3 py-2 text-left hover:bg-[#F9FAFB] flex items-center gap-2"
                      >
                        <div className={`w-2 h-2 rounded-full ${tier.id === currentTier ? 'bg-[#EA580C]' : 'bg-[#E5E7EB]'}`} />
                        <div className="flex-1 min-w-0">
                          <div className="text-[13px] font-medium text-[#111827]">{tier.label}</div>
                          <div className="text-[11px] text-[#9CA3AF]">{tier.desc}</div>
                        </div>
                        {tier.id === currentTier && <Check className="w-3.5 h-3.5 text-[#EA580C]" />}
                      </button>
                    ))}
                    <div className="my-1 border-t border-[#F3F4F6]" />
                    <button
                      onClick={() => { setShowReextractDropdown(false); onReset(); }}
                      disabled={!selectedDoc?._hasEdits}
                      className="w-full px-3 py-2 text-left text-[13px] text-[#4B5563] hover:bg-[#F9FAFB] flex items-center gap-2 disabled:opacity-40 disabled:cursor-not-allowed"
                    >
                      <Undo2 className="w-3.5 h-3.5" />
                      Reset all edits to original
                    </button>
                  </div>
                )}
              </div>

              {/* Export dropdown */}
              <div className="relative export-dropdown-container">
                <button
                  onClick={() => setShowExportDropdown(!showExportDropdown)}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-[#EA580C] hover:bg-[#C2410C] text-white rounded-md text-[13px] font-semibold transition-colors"
                >
                  <Download className="w-3.5 h-3.5" />
                  Export
                  <ChevronDown className={`w-3 h-3 transition-transform ${showExportDropdown ? 'rotate-180' : ''}`} />
                </button>
                {showExportDropdown && (
                  <div className="absolute right-0 bottom-full mb-1 w-64 bg-white border border-[#E5E7EB] rounded-md shadow-lg py-1 z-40 sf-fade-in">
                    <div className="px-3 py-1.5 text-[10px] font-semibold text-[#9CA3AF] uppercase tracking-wide">
                      Format
                    </div>
                    {[
                      { f: 'original-layout', label: 'Original Layout', sub: 'Coming soon — verbatim source', Icon: FileSpreadsheet, tint: 'text-[#9CA3AF] bg-[#F3F4F6]', disabled: true },
                      { f: 'xlsx', label: 'SyproFlow Format', sub: 'Structured for analysis', Icon: FileSpreadsheet, tint: 'text-emerald-600 bg-emerald-50' },
                      { f: 'csv',  label: 'CSV (.csv)',       sub: 'Universal format',       Icon: FileText,        tint: 'text-blue-600 bg-blue-50' },
                      { f: 'json', label: 'JSON (.json)',     sub: 'For developers',         Icon: File,            tint: 'text-purple-600 bg-purple-50' },
                      { f: 'docx', label: 'Word (.docx)',     sub: 'Coming soon — branded, editable', Icon: FileText, tint: 'text-[#9CA3AF] bg-[#F3F4F6]', disabled: true },
                    ].map(opt => (
                      <button
                        key={opt.f}
                        onClick={() => !opt.disabled && handleQuickExport(opt.f)}
                        disabled={opt.disabled}
                        title={opt.disabled ? 'Coming soon' : undefined}
                        className={`w-full px-3 py-2 text-left flex items-center gap-3 ${opt.disabled ? 'opacity-50 cursor-not-allowed' : 'hover:bg-[#F9FAFB]'}`}
                      >
                        <div className={`w-8 h-8 rounded-md flex items-center justify-center ${opt.tint}`}>
                          <opt.Icon className="w-4 h-4" />
                        </div>
                        <div className="min-w-0">
                          <div className="text-[13px] font-medium text-[#111827]">{opt.label}</div>
                          <div className="text-[11px] text-[#9CA3AF]">{opt.sub}</div>
                        </div>
                      </button>
                    ))}
                    {/* Tally Excel with voucher type selector */}
                    <div className="my-1 border-t border-[#F3F4F6]" />
                    <div className="px-3 py-1.5 text-[10px] font-semibold text-[#9CA3AF] uppercase tracking-wide">
                      Tally Formats
                    </div>
                    <div className={`px-3 py-2 ${selectedDoc?.tables?.length ? 'hover:bg-[#F9FAFB]' : 'opacity-50 cursor-not-allowed'}`}
                      title={selectedDoc?.tables?.length ? undefined : 'Tally export requires tabular data (invoices, statements, vouchers)'}
                    >
                      <div className="flex items-center gap-3 mb-2">
                        <div className="w-8 h-8 rounded-md bg-[#FFF7ED] flex items-center justify-center">
                          <FileSpreadsheet className="w-4 h-4 text-[#EA580C]" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="text-[13px] font-medium text-[#111827]">Tally Excel</div>
                          <div className="text-[11px] text-[#9CA3AF]">{selectedDoc?.tables?.length ? 'Direct import to TallyPrime' : 'Requires tabular data'}</div>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <select
                          value={voucherTypeOverrides[selectedDoc?.id] || 'Auto'}
                          onChange={(e) => {
                            const val = e.target.value;
                            setVoucherTypeOverrides(prev => ({
                              ...prev,
                              [selectedDoc?.id]: val === 'Auto' ? null : val
                            }));
                          }}
                          onClick={(e) => e.stopPropagation()}
                          className="flex-1 px-2 py-1 text-[11px] border border-[#E5E7EB] rounded focus:outline-none focus:border-[#EA580C] bg-white"
                        >
                          <option value="Auto">Auto-detect voucher</option>
                          <option value="Sales">Sales</option>
                          <option value="Purchase">Purchase</option>
                          <option value="Bank Statement">Bank Statement</option>
                          <option value="Credit Note">Credit Note</option>
                          <option value="Debit Note">Debit Note</option>
                          <option value="Journal">Journal</option>
                          <option value="Expense">Expense</option>
                        </select>
                        <button
                          onClick={() => selectedDoc?.tables?.length && handleQuickExport('tally-excel')}
                          disabled={!selectedDoc?.tables?.length}
                          className={`px-2.5 py-1 rounded text-[11px] font-semibold ${selectedDoc?.tables?.length ? 'bg-[#EA580C] hover:bg-[#C2410C] text-white' : 'bg-[#E5E7EB] text-[#9CA3AF] cursor-not-allowed'}`}
                        >
                          Excel
                        </button>
                        <button
                          onClick={() => selectedDoc?.tables?.length && handleQuickExport('tally')}
                          disabled={!selectedDoc?.tables?.length}
                          className={`px-2.5 py-1 rounded text-[11px] font-semibold ${selectedDoc?.tables?.length ? 'bg-[#EA580C] hover:bg-[#C2410C] text-white' : 'bg-[#E5E7EB] text-[#9CA3AF] cursor-not-allowed'}`}
                        >
                          XML
                        </button>
                      </div>
                      {!voucherTypeOverrides[selectedDoc?.id] && (
                        hasGstin
                          ? <p className="mt-1 text-[10px] text-[#9CA3AF]">GSTIN matching will classify automatically.</p>
                          : <p className="mt-1 text-[10px] text-[#B45309]">Set a GSTIN in Settings for auto-classification.</p>
                      )}
                    </div>
                  </div>
                )}
              </div>

              {/* Preview & Push -- navigate to voucher preview (hidden for bank statements) */}
              {tallyEnabled && !isBankStatement && (
                <button
                  onClick={onPreviewAndPush}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-white border border-[#EA580C] text-[#EA580C] hover:bg-[#FFF7ED] rounded-md text-[13px] font-semibold transition-colors"
                  title="Preview voucher before pushing to Tally"
                >
                  <Eye className="w-3.5 h-3.5" />
                  <span className="hidden sm:inline">Preview & Push</span>
                </button>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}

// =============================================================================
// SettingsDrawer — slide-in from right. Holds Processing/Tier/Batch/Tally/Brand.
// =============================================================================

function SettingsDrawer({
  isOpen, onClose,
  extractionMode, setExtractionMode,
  modelTier, setModelTier,
  companySettings, setCompanySettings,
  onOpenTallySettings,
  tallyBridgeStatus,
  onLogout, userEmail
}) {
  const navigate = useNavigate();
  if (!isOpen) return null;

  const tierInfo = {
    standard: {
      label: 'Standard', desc: 'Best for digital invoices & bank statements.',
      pricing: '₹0.80 per document · up to 300/month',
      note: 'Up to 100 pages · 40 MB · best for clear documents',
      accuracy: '100% on digital invoices'
    },
    professional: {
      label: 'Professional', desc: 'Recommended for all document types.',
      pricing: '₹3.50 per document · unlimited',
      note: 'No page limit · 50+ languages · scanned & photographed docs',
      accuracy: '98.5% on scanned foreign invoices'
    },
    premium: {
      label: 'Premium', desc: 'Maximum accuracy. For high-value documents.',
      pricing: '₹8.50 per document · unlimited',
      note: 'Poor quality scans · handwriting · mixed-language',
      accuracy: 'Highest accuracy available'
    }
  };

  const update = (patch) => setCompanySettings(prev => ({ ...prev, ...patch }));

  return (
    <div className="fixed inset-0 z-50 flex" onClick={onClose}>
      <div className="flex-1" />
      <div
        className="w-full max-w-md h-full bg-white shadow-2xl flex flex-col sf-slide-right"
        onClick={e => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex-shrink-0 flex items-center justify-between px-5 py-3 border-b border-[#E5E7EB]">
          <h2 className="text-[16px] font-semibold text-[#111827]">Settings</h2>
          <button onClick={onClose} className="p-1.5 hover:bg-[#F9FAFB] rounded-md">
            <X className="w-4 h-4 text-[#4B5563]" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-5 space-y-6">
          {/* Processing mode */}
          <section>
            <h3 className="text-[11px] font-semibold text-[#9CA3AF] uppercase tracking-wide mb-2">Processing mode</h3>
            <div className="space-y-2">
              {[
                { id: 'auto', label: 'Default', desc: 'For invoices, bills, bank statements and general documents.' },
                { id: 'exam', label: 'Exam Results', desc: 'Specialised extractor for university mark sheets and exam results.' }
              ].map(m => (
                <label
                  key={m.id}
                  className={`block p-3 rounded-md border cursor-pointer transition-colors ${
                    extractionMode === m.id ? 'border-[#EA580C] bg-[#FFF7ED]' : 'border-[#E5E7EB] hover:bg-[#F9FAFB]'
                  }`}
                >
                  <div className="flex items-start gap-2.5">
                    <input
                      type="radio"
                      name="sf-mode"
                      checked={extractionMode === m.id}
                      onChange={() => setExtractionMode(m.id)}
                      className="mt-0.5 w-4 h-4 accent-[#EA580C]"
                    />
                    <div>
                      <div className="text-[13px] font-semibold text-[#111827]">{m.label}</div>
                      <div className="text-[12px] text-[#4B5563] mt-0.5">{m.desc}</div>
                    </div>
                  </div>
                </label>
              ))}
            </div>
          </section>

          {/* Tier */}
          <section>
            <h3 className="text-[11px] font-semibold text-[#9CA3AF] uppercase tracking-wide mb-2">Default extraction tier</h3>
            <div className="space-y-2">
              {Object.entries(tierInfo).map(([id, info]) => (
                <label
                  key={id}
                  className={`block p-3 rounded-md border cursor-pointer transition-colors ${
                    modelTier === id ? 'border-[#EA580C] bg-[#FFF7ED]' : 'border-[#E5E7EB] hover:bg-[#F9FAFB]'
                  }`}
                >
                  <div className="flex items-start gap-2.5">
                    <input
                      type="radio"
                      name="sf-tier"
                      checked={modelTier === id}
                      onChange={() => setModelTier(id)}
                      className="mt-0.5 w-4 h-4 accent-[#EA580C]"
                    />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between">
                        <div className="text-[13px] font-semibold text-[#111827]">{info.label}</div>
                        <div className="text-[11px] text-[#EA580C] font-semibold">{info.pricing.split(' · ')[0]}</div>
                      </div>
                      <div className="text-[12px] text-[#4B5563] mt-0.5">{info.desc}</div>
                      <div className="text-[11px] text-[#9CA3AF] mt-1">{info.note}</div>
                    </div>
                  </div>
                </label>
              ))}
            </div>
          </section>

          {/* Batch processing */}
          <section>
            <h3 className="text-[11px] font-semibold text-[#9CA3AF] uppercase tracking-wide mb-2">Batch processing</h3>
            <label className="flex items-start gap-3 p-3 rounded-md border border-[#E5E7EB]">
              <input
                type="checkbox"
                checked={!!companySettings.batchEnabled}
                onChange={(e) => update({ batchEnabled: e.target.checked })}
                className="mt-0.5 w-4 h-4 accent-[#EA580C]"
              />
              <div>
                <div className="text-[13px] font-semibold text-[#111827]">Enable batch upload</div>
                <div className="text-[12px] text-[#4B5563] mt-0.5">
                  When enabled, you can queue multiple documents and extract them in one pass.
                </div>
              </div>
            </label>
          </section>

          {/* Tally integration */}
          <section>
            <h3 className="text-[11px] font-semibold text-[#9CA3AF] uppercase tracking-wide mb-2">Tally integration</h3>
            <label className="flex items-start gap-3 p-3 rounded-md border border-[#E5E7EB] mb-2">
              <input
                type="checkbox"
                checked={!!companySettings.tallyEnabled}
                onChange={(e) => update({ tallyEnabled: e.target.checked })}
                className="mt-0.5 w-4 h-4 accent-[#EA580C]"
              />
              <div className="flex-1">
                <div className="text-[13px] font-semibold text-[#111827]">Enable Tally Push</div>
                <div className="text-[12px] text-[#4B5563] mt-0.5">
                  Show the Push-to-Tally action. Requires the local Tally Bridge app.
                </div>
              </div>
            </label>

            {companySettings.tallyEnabled && (
              <>
                <div className="p-3 rounded-md bg-[#F9FAFB] border border-[#E5E7EB] mb-2">
                  <div className="flex items-center gap-2">
                    <span className={`w-2 h-2 rounded-full ${
                      tallyBridgeStatus.connected
                        ? (tallyBridgeStatus.tallyConnected ? 'bg-emerald-500' : 'bg-amber-500')
                        : 'bg-slate-300'
                    }`} />
                    <span className="text-[12px] font-medium text-[#111827]">
                      {tallyBridgeStatus.connected
                        ? (tallyBridgeStatus.tallyConnected
                            ? `Connected · ${tallyBridgeStatus.company || 'Tally'}`
                            : 'Bridge running · Tally not connected')
                        : 'Bridge not detected'}
                    </span>
                  </div>
                  {!tallyBridgeStatus.connected && (
                    <div className="text-[11px] text-[#9CA3AF] mt-1.5">
                      Start <span className="font-mono">tally_bridge.exe</span> on this computer and it will show up here.
                      <div className="mt-1">
                        Don&apos;t have it yet?{' '}
                        <button
                          onClick={() => navigate('/download')}
                          className="text-[#EA580C] hover:underline font-medium bg-transparent border-none p-0 cursor-pointer text-[11px]"
                        >
                          Download the Tally Bridge
                        </button>
                      </div>
                    </div>
                  )}
                </div>
                <button
                  onClick={onOpenTallySettings}
                  className="w-full flex items-center justify-center gap-2 py-2 bg-white border border-[#E5E7EB] hover:border-[#EA580C] hover:text-[#EA580C] text-[#4B5563] rounded-md text-[13px] font-semibold transition-colors"
                >
                  <Building2 className="w-3.5 h-3.5" />
                  GSTIN &amp; voucher settings
                </button>
              </>
            )}
          </section>

          {/* Word export branding */}
          <section>
            <h3 className="text-[11px] font-semibold text-[#9CA3AF] uppercase tracking-wide mb-2">Word export branding</h3>
            <p className="text-[12px] text-[#4B5563] mb-3">Appears on branded Word documents (your letterhead for exports).</p>
            <div className="space-y-2">
              <div>
                <label className="block text-[11px] font-semibold text-[#4B5563] mb-1">Company name</label>
                <input
                  type="text"
                  value={companySettings.brandCompanyName || ''}
                  onChange={(e) => update({ brandCompanyName: e.target.value })}
                  placeholder="SyproOne Solutions Pvt Ltd"
                  className="w-full px-3 py-2 text-[13px] border border-[#E5E7EB] rounded-md focus:outline-none focus:border-[#EA580C]"
                />
              </div>
              <div>
                <label className="block text-[11px] font-semibold text-[#4B5563] mb-1">Tagline</label>
                <input
                  type="text"
                  value={companySettings.brandTagline || ''}
                  onChange={(e) => update({ brandTagline: e.target.value })}
                  placeholder="Documents In. Data Out."
                  className="w-full px-3 py-2 text-[13px] border border-[#E5E7EB] rounded-md focus:outline-none focus:border-[#EA580C]"
                />
              </div>
              <div>
                <label className="block text-[11px] font-semibold text-[#4B5563] mb-1">Footer</label>
                <input
                  type="text"
                  value={companySettings.brandFooter || ''}
                  onChange={(e) => update({ brandFooter: e.target.value })}
                  placeholder="www.syproflow.com · contact@syproflow.com"
                  className="w-full px-3 py-2 text-[13px] border border-[#E5E7EB] rounded-md focus:outline-none focus:border-[#EA580C]"
                />
              </div>
            </div>
          </section>

          {/* Account */}
          <section>
            <h3 className="text-[11px] font-semibold text-[#9CA3AF] uppercase tracking-wide mb-2">Account</h3>
            <div className="p-3 rounded-md bg-[#F9FAFB] border border-[#E5E7EB]">
              <div className="text-[11px] text-[#9CA3AF]">Signed in as</div>
              <div className="text-[13px] font-medium text-[#111827] truncate">{userEmail || '—'}</div>
            </div>
            <button
              onClick={() => {
                if (window.confirm('Sign out of SyproFlow?')) onLogout();
              }}
              className="mt-2 w-full flex items-center justify-center gap-2 py-2 bg-white border border-red-200 text-[#EF4444] hover:bg-red-50 rounded-md text-[13px] font-semibold transition-colors"
            >
              <LogOut className="w-3.5 h-3.5" />
              Sign out
            </button>
          </section>

          {/* Help */}
          <section>
            <h3 className="text-[11px] font-semibold text-[#9CA3AF] uppercase tracking-wide mb-2">Help</h3>
            <div className="space-y-1">
              <a
                href="https://syproflow.com/docs"
                target="_blank"
                rel="noreferrer"
                className="flex items-center gap-2 py-2 px-3 text-[13px] text-[#111827] hover:bg-[#F9FAFB] rounded-md"
              >
                <HelpCircle className="w-3.5 h-3.5 text-[#9CA3AF]" />
                Documentation
              </a>
              <a
                href="mailto:contact@syproflow.com"
                className="flex items-center gap-2 py-2 px-3 text-[13px] text-[#111827] hover:bg-[#F9FAFB] rounded-md"
              >
                <HelpCircle className="w-3.5 h-3.5 text-[#9CA3AF]" />
                Contact support
              </a>
            </div>
          </section>
        </div>
      </div>
    </div>
  );
}

// =============================================================================
// Auth-aware routing (verbatim from v7)
// =============================================================================

function ProtectedRoute({ children }) {
  const token = localStorage.getItem('syproflow_token');
  if (!token) return <Navigate to="/login" replace />;

  const SESSION_TIMEOUT_MS = 24 * 60 * 60 * 1000; // 24 hours
  const lastActivity = parseInt(localStorage.getItem('syproflow_last_activity') || '0');
  if (Date.now() - lastActivity > SESSION_TIMEOUT_MS) {
    localStorage.removeItem('syproflow_token');
    localStorage.removeItem('syproflow_email');
    localStorage.removeItem('syproflow_last_activity');
    return <Navigate to="/login" replace />;
  }
  localStorage.setItem('syproflow_last_activity', Date.now().toString());

  return children;
}

export default function SyproFlowApp() {
  const navigate = useNavigate();
  const [authReady, setAuthReady] = useState(false);

  useEffect(() => {
    const token = localStorage.getItem('syproflow_token');
    if (!token) { setAuthReady(true); return; }
    const apiBase = getApiBaseUrl();
    fetch(`${apiBase}/auth/me`, { headers: { 'Authorization': `Bearer ${token}` } })
      .then(res => {
        if (!res.ok) {
          localStorage.removeItem('syproflow_token');
          localStorage.removeItem('syproflow_email');
        }
        setAuthReady(true);
      })
      .catch(() => setAuthReady(true));
  }, []);

  const handleLogin = (data) => {
    localStorage.setItem('syproflow_last_activity', Date.now().toString());
    if (data.is_new_user) navigate('/welcome');
    else navigate('/app');
  };

  if (!authReady) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#F9FAFB]">
        <div className="text-center">
          <div className="w-8 h-8 border-2 border-[#EA580C] border-t-transparent rounded-full animate-spin mx-auto mb-3" />
          <p className="text-[13px] text-[#4B5563]">Loading…</p>
        </div>
      </div>
    );
  }

  return (
    <Routes>
      <Route
        path="/login"
        element={
          localStorage.getItem('syproflow_token')
            ? <Navigate to="/app" replace />
            : <LoginPage onLogin={handleLogin} />
        }
      />
      <Route
        path="/welcome"
        element={
          <ProtectedRoute>
            <WelcomeScreen onComplete={() => navigate('/app')} />
          </ProtectedRoute>
        }
      />
      <Route
        path="/app"
        element={
          <ProtectedRoute>
            <SyproFlowMainApp />
          </ProtectedRoute>
        }
      />
      <Route
        path="/preview/:docId"
        element={
          <ProtectedRoute>
            <VoucherPreview />
          </ProtectedRoute>
        }
      />
      <Route
        path="/download"
        element={
          <ProtectedRoute>
            <DownloadBridge />
          </ProtectedRoute>
        }
      />
      <Route path="*" element={<Navigate to={localStorage.getItem('syproflow_token') ? '/app' : '/login'} replace />} />
    </Routes>
  );
}

/*
 * =============================================================================
 * CHANGELOG — v8.0.0 (2026-04)
 * =============================================================================
 *
 * TOP BAR
 *   - Logo colour changed from indigo/purple gradient to solid orange #EA580C
 *   - Logo wordmark changed from split "Sypro" + "Flow" (purple) to solid orange
 *   - Stripped Auto/Exam toggle, tier selector, Batch button, Extract All,
 *     Export dropdown, Push-to-Tally, and trash-all from the header
 *   - Added: centered document name + status pill, Exam-mode tag, "Edited" tag
 *   - Added: Settings gear + user-menu dropdown (Account, Support, Sign Out)
 *
 * SIDEBAR
 *   - Collapsible: 220/240px expanded, 44px icon-only collapsed
 *     (state persists in localStorage.syproflow_sidebar_collapsed)
 *   - Landing state (zero docs): full-width dropzone with drag-and-drop
 *   - Document-loaded state: compact rows, selected doc has orange left border,
 *     "+ Upload another" button at the bottom
 *
 * SOURCE PREVIEW (new)
 *   - Renders inline alongside the data panel (desktop) or as a tab (mobile)
 *   - PDFs use native <iframe> with browser's built-in viewer
 *     (no pdfjs-dist dependency added)
 *   - Images use <img> with CSS zoom
 *   - Zoom toolbar: Fit / 100% / 150%
 *
 * DATA PANEL
 *   - Summary tab + one tab per extracted table (was a single scrolling view)
 *   - Summary tab buckets metadata into Document / Seller / Buyer / Shipping /
 *     Totals / Other; includes Bank Details, Total in Words, Terms; extraction
 *     stats (rows, tables, fields, confidence) sit at the bottom
 *   - Table tab: sticky header, alternating row shading, right-aligned numeric
 *     columns (monospace), click-to-edit cells
 *
 * EDITING
 *   - Orange dot on edited cells; hover tooltip shows the original value
 *   - Low-confidence cells get a yellow underline (replaces full-cell tint)
 *   - Tab / Shift+Tab: move horizontally (with row wrap)
 *   - Enter: commit + move to same column on the next row
 *   - Escape: cancel edit
 *   - Ctrl+Z: undo most recent cell edit or row deletion (session history)
 *   - Row hover reveals Delete + Duplicate actions
 *
 * BOTTOM ACTION BAR (new)
 *   - View Source toggle, Undo button, Re-extract dropdown, Export dropdown,
 *     Push-to-Tally (gated on companySettings.tallyEnabled)
 *   - Re-extract dropdown includes "Reset all edits to original extraction"
 *
 * SETTINGS DRAWER (new)
 *   - Slide-in from the right; consolidates Processing mode, Default tier,
 *     Batch Processing toggle, Tally integration + bridge status + GSTIN
 *     sub-dialog, Word Export Branding, Account, Help
 *
 * MOBILE
 *   - Single-column tabbed layout: Source | Data | Docs
 *   - Docs tab has Camera + Files buttons and a full-screen document list
 *   - Bottom action bar retains Export + Push-to-Tally (no View Source toggle)
 *
 * TYPOGRAPHY
 *   - Inter font via Google Fonts @import (no npm dep)
 *   - Size scale 11 / 13 / 14 / 16 / 20 px
 *   - Weights 400 / 500 / 600 / 700
 *
 * PRESERVED (zero-change zones)
 *   - createImageBitmap client-side compression for Android camera uploads
 *   - FormData with raw Blob + filename argument (no new File())
 *   - Synchronous handleFileSelect (Android camera file ref stays valid)
 *   - DOM-append-before-click download pattern on every format
 *   - export-dropdown-container class for the click-outside handler
 *   - All extraction endpoints, payloads, auth headers
 *   - 24-hour session timeout in ProtectedRoute
 *   - Tally Bridge form POST to http://localhost:8765/approve
 *   - Voucher classification logic (classifyVoucherType)
 *
 * OPEN ITEMS (flagged, deferred)
 *   - Arrow-key navigation without editing (requires separate "selected cell"
 *     model). Click + Tab/Enter handle most flows.
 *   - Ctrl+Y / Ctrl+Shift+Z redo (inverse stack). Undo-only for v8.0.
 *   - Ctrl+scroll-to-zoom on the source preview (would require pdfjs-dist)
 */